/**
 * TodoList.js - To-Do 리스트 페이지 (User)
 * 스프레드시트 형식으로 To-Do 리스트 관리 및 자주하는 업무 등록
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Tooltip,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Paper,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ListItem,
  ListItemText,
  Divider,
  InputAdornment,
} from '@mui/material';
import { Plus, Trash2, Clock, CheckCircle, Download, Edit2, Filter, CheckSquare, Save, Search, Grid, RefreshCw, ChevronUp, ChevronDown, X } from 'react-feather';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import { useMobileView } from '../contexts/MobileViewContext';
import axios from 'axios';
import { format, isToday, isTomorrow, addDays, startOfDay, differenceInDays, subDays, subMonths, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import { ko } from 'date-fns/locale';
import ResizableTableRow from '../components/ResizableTableRow';
import { readExcelFile, downloadExcelTemplate, TABLE_COLUMNS } from '../utils/excelUtils';
import TodoBottomSheet from '../components/TodoBottomSheet';
import { Snackbar, Alert, FormControl, Select, MenuItem } from '@mui/material';
import ConfirmModal from '../components/ConfirmModal';
import InfoModal from '../components/InfoModal';
import SwipeableListItem from '../components/SwipeableListItem';
import TodoBadgeSystem from '../components/TodoBadgeSystem';
import * as XLSX from 'xlsx';
import { useMemo } from 'react';
import apiConfig from '../config/api';
import { validateData, TodoCreateSchema, TodoUpdateSchema } from '../utils/validation';

const API_BASE_URL = apiConfig.V1;

const TodoList = () => {
  const { colors, isDark } = useCustomTheme();
  const { isMobileView } = useMobileView();
  
  const [todos, setTodos] = useState([]);
  const [includeCompleted, setIncludeCompleted] = useState(false); // 완료 포함 여부
  
  // 날짜 범위 초기값 설정
  const getDefaultStartDate = () => {
    const today = new Date();
    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);
    return format(sevenDaysAgo, 'yyyy-MM-dd');
  };
  const getDefaultEndDate = () => format(new Date(), 'yyyy-MM-dd');
  
  const [dateRange, setDateRange] = useState({ 
    startDate: getDefaultStartDate(), 
    endDate: getDefaultEndDate() 
  }); // 조회 기간
  const [dateRangeModalOpen, setDateRangeModalOpen] = useState(false); // 날짜 범위 선택 모달
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [newRow, setNewRow] = useState(null);
  const [selectedTodoForLog, setSelectedTodoForLog] = useState(null);
  const [logDialogOpen, setLogDialogOpen] = useState(false);
  
  // 정렬 상태
  const [sortConfig, setSortConfig] = useState({ column: 'created_at', direction: 'desc' });
  const [mobileSortOption] = useState('date');
  
  // 통합 검색 및 계약 관련 상태
  const [contractSearchQuery] = useState('');
  const [contractOptions, setContractOptions] = useState([]);
  const [recentContracts, setRecentContracts] = useState([]);
  const [isSearchingContracts, setIsSearchingContracts] = useState(false);
  
  // 다중 행 입력 상태 관리
  const [inputRows, setInputRows] = useState([
    {
      id: Date.now(),
      company_code: '',
      contract_id: '',
      work_description: '', // 업무 상세내용 (note_1로 저장됨)
      note_1: '', // 비고 (업무 기한 등 참고사항)
      job_category: '', // 업무 구분
    }
  ]);
  
  // 선택된 행 관리
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  
  // 삭제 확인 모달 상태
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteConfirmCallback, setDeleteConfirmCallback] = useState(null);
  const [deleteConfirmMessage, setDeleteConfirmMessage] = useState('');
  
  // 정보 모달 상태 (성공/알림 메시지)
  const [infoModalOpen, setInfoModalOpen] = useState(false);
  const [infoModalMessage, setInfoModalMessage] = useState('');
  const [infoModalTitle, setInfoModalTitle] = useState('알림');
  
  // 테이블 관련 상태 - 퍼센트 기반 동적 너비
  const [tableWidth, setTableWidth] = useState(0);
  const [editingRowId, setEditingRowId] = useState(null);
  
  // 필터링용 콤보 박스 상태
  const [companies, setCompanies] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [logFormData, setLogFormData] = useState({
    log_date: new Date().toISOString().split('T')[0],
    input_minute: 0,
    input_description: '',
  });
  // 테이블 너비 동적 계산 (퍼센트 기반) - 투입시간 목록과 유사한 구조
  const columnWidthPercentages = {
    todo_id: 5,
    company_code: 7,
    company_name: 9,
    contract_id: 7,
    contract_title: 11, // 계약 명칭 너비 감소
    job_category: 8, // 업무 구분 추가
    note_1: 16, // 업무 상세내용 너비 감소
    note_2: 12, // 비고
    created_at: 10, // 생성일시 너비 감소
    status: 10, // 진행경과 너비 증가
    actions: 7,
  };
  
  const [rowHeights, setRowHeights] = useState({});
  
  // 모바일 최적화 상태
  const [bottomSheetOpen, setBottomSheetOpen] = useState(false);
  const [editingTodo, setEditingTodo] = useState(null);
  const [todoToDelete, setTodoToDelete] = useState(null);
  const [deletedTodo, setDeletedTodo] = useState(null); // Undo용
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  // 테이블 컨테이너 너비 계산
  useEffect(() => {
    const updateTableWidth = () => {
      const tableContainer = document.querySelector('[data-table-container="todo"]');
      if (tableContainer) {
        setTableWidth(tableContainer.offsetWidth);
      }
    };

    updateTableWidth();
    window.addEventListener('resize', updateTableWidth);
    const timer = setInterval(updateTableWidth, 100);

    return () => {
      window.removeEventListener('resize', updateTableWidth);
      clearInterval(timer);
    };
  }, []);

  // 최근 사용 계약 조회
  useEffect(() => {
    fetchRecentContracts();
  }, []);

  // 계약 검색 (디바운싱)
  useEffect(() => {
    if (!contractSearchQuery || contractSearchQuery.length < 2) {
      setContractOptions([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      searchContracts(contractSearchQuery);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [contractSearchQuery]);

  useEffect(() => {
    fetchCompanies();
    fetchContracts();
  }, []);

  // To-Do 목록 자동 로딩 (컴포넌트 마운트 시 및 완료 포함 여부 변경 시)
  useEffect(() => {
    fetchTodos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeCompleted]);

  // RightSidebar에서 To-Do 추가 이벤트 수신
  useEffect(() => {
    const handleAddTodosToTodoList = (event) => {
      const { todos: selectedTodos } = event.detail;
      if (!selectedTodos || selectedTodos.length === 0) return;

      console.log('[handleAddTodosToTodoList] 받은 todos:', selectedTodos);
      console.log('[handleAddTodosToTodoList] 현재 contracts:', contracts);
      console.log('[handleAddTodosToTodoList] 현재 companies:', companies);

      // To-Do 항목들을 기반으로 새 행들 생성
      const newRows = selectedTodos.map((todo, index) => {
        // 계약 정보 찾기 (contracts 배열에서 contract_id로 찾기)
        let contract = null;
        if (todo.contract_id) {
          contract = contracts.find(c => c.contract_id === todo.contract_id);
          
          // 계약을 찾았을 때, company_name_kr 추가
          if (contract) {
            // 업체 정보 찾기
            const company = companies.find(c => c.company_code === contract.company_code || c.company_code === todo.company_code);
            
            // 계약 객체에 company_name_kr과 display_label 추가
            contract = {
              ...contract,
              company_name_kr: company?.company_name_kr || contract.company_code || todo.company_code || '',
              display_label: `${company?.company_name_kr || contract.company_code || todo.company_code || ''} - ${contract.contract_title || contract.contract_id || ''}`,
            };
          }
        }
        
        // 계약을 찾지 못한 경우, company_code와 contract_id로 계약 객체 생성
        if (!contract && todo.company_code && todo.contract_id) {
          // 업체 정보 찾기
          const company = companies.find(c => c.company_code === todo.company_code);
          // 계약 객체 생성 (최소한의 정보라도 포함)
          contract = {
            contract_id: todo.contract_id,
            company_code: todo.company_code,
            company_name_kr: company?.company_name_kr || todo.company_code,
            contract_title: todo.contract_id, // contract_title이 없으므로 contract_id 사용
            display_label: `${company?.company_name_kr || todo.company_code} - ${todo.contract_id}`,
          };
        }

        console.log('[handleAddTodosToTodoList] 생성된 contract:', contract);

        return {
          id: Date.now() + index, // 고유 ID 생성 (Math.random() 대신 index 사용)
          company_code: todo.company_code || '',
          contract_id: todo.contract_id || '',
          contract: contract, // 계약 객체 전체 저장
          work_description: todo.note_1 || '', // 업무 상세내용
          note_1: todo.note_2 || '', // 비고
          job_category: todo.job_category || '', // 업무 구분 추가
        };
      });

      console.log('[handleAddTodosToTodoList] 생성된 newRows:', newRows);

      // 기존 행에 새 행 추가
      setInputRows(prev => [...prev, ...newRows]);
    };

    window.addEventListener('addTodosToTodoList', handleAddTodosToTodoList);
    
    return () => {
      window.removeEventListener('addTodosToTodoList', handleAddTodosToTodoList);
    };
  }, [companies, contracts]);

  const fetchTodos = async () => {
    try {
      // 완료 포함 여부에 따라 status 파라미터 전달
      let url = `${API_BASE_URL}/todos`;
      if (!includeCompleted) {
        // 완료 포함이 false이면 active만 조회
        url += '?status=active';
      }
      // 완료 포함이 true이면 파라미터 없이 모든 상태 조회
      
      console.log('[fetchTodos] 요청 URL:', url);
      console.log('[fetchTodos] includeCompleted:', includeCompleted);
      
      const response = await axios.get(url);
      const data = response.data || [];
      console.log('[fetchTodos] 조회된 To-Do 개수:', data.length);
      console.log('[fetchTodos] 조회된 상태들:', data.map(t => t.status) || []);
      
      // job_category 확인 로깅
      if (data.length > 0) {
        const firstRecord = data[0];
        console.log('[fetchTodos] ===== job_category 확인 =====');
        console.log('[fetchTodos] 첫 번째 레코드 전체:', firstRecord);
        console.log('[fetchTodos] 첫 번째 레코드 키 목록:', Object.keys(firstRecord));
        console.log('[fetchTodos] job_category 값:', firstRecord.job_category, '(타입:', typeof firstRecord.job_category, ')');
        console.log('[fetchTodos] job_category가 undefined인가?', firstRecord.job_category === undefined);
        console.log('[fetchTodos] job_category가 null인가?', firstRecord.job_category === null);
        console.log('[fetchTodos] job_category가 빈 문자열인가?', firstRecord.job_category === '');
        
        const recordsWithJobCategory = data.filter(r => r.job_category && r.job_category.trim() !== '');
        console.log('[fetchTodos] job_category가 있는 레코드 수:', recordsWithJobCategory.length, '/', data.length);
        if (recordsWithJobCategory.length > 0) {
          console.log('[fetchTodos] job_category가 있는 첫 번째 레코드:', recordsWithJobCategory[0]);
        }
        console.log('[fetchTodos] ================================');
      }
      
      setTodos(data);
    } catch (error) {
      console.error('To-Do 조회 실패:', error);
      console.error('에러 상세:', error.response?.data);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/customers`);
      setCompanies(response.data || []);
    } catch (error) {
      console.error('업체 목록 조회 실패:', error);
    }
  };

  const fetchContracts = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/contracts`);
      setContracts(response.data || []);
    } catch (error) {
      console.error('계약 목록 조회 실패:', error);
    }
  };


  // 최근 사용 계약 조회
  const fetchRecentContracts = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/contracts/recent`, { params: { limit: 5 } });
      setRecentContracts(response.data || []);
    } catch (error) {
      console.error('최근 계약 조회 실패:', error);
    }
  };

  // 계약 검색
  const searchContracts = async (query) => {
    if (!query || query.length < 2) return;
    
    setIsSearchingContracts(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/contracts/search`, { params: { q: query } });
      setContractOptions(response.data || []);
    } catch (error) {
      console.error('계약 검색 실패:', error);
      setContractOptions([]);
    } finally {
      setIsSearchingContracts(false);
    }
  };

  // 컬럼 너비 계산 함수 (퍼센트 기반)
  const getColumnWidth = useCallback((columnKey) => {
    if (tableWidth === 0) return 'auto';
    const percentage = columnWidthPercentages[columnKey] || 8;
    return `${(tableWidth * percentage) / 100}px`;
  }, [tableWidth, columnWidthPercentages]);

  // 정렬된 todos 계산
  const sortedTodos = useMemo(() => {
    if (!todos || todos.length === 0) return [];
    
    const sorted = [...todos].sort((a, b) => {
      // 모바일 정렬 옵션 처리
      if (isMobileView) {
        if (mobileSortOption === 'date') {
          const aDate = a.created_at ? new Date(a.created_at).getTime() : 0;
          const bDate = b.created_at ? new Date(b.created_at).getTime() : 0;
          return bDate - aDate; // 최신순
        } else if (mobileSortOption === 'company') {
          const aCompany = (a.company_code || '').toLowerCase();
          const bCompany = (b.company_code || '').toLowerCase();
          if (aCompany < bCompany) return -1;
          if (aCompany > bCompany) return 1;
          return 0;
        }
      }
      
      // PC 정렬 처리
      const { column, direction } = sortConfig;
      let aValue = a[column];
      let bValue = b[column];
      
      // null/undefined 처리
      if (aValue == null) aValue = '';
      if (bValue == null) bValue = '';
      
      // 날짜/시간 필드 처리
      if (column === 'created_at' || column === 'updated_at') {
        aValue = aValue ? new Date(aValue).getTime() : 0;
        bValue = bValue ? new Date(bValue).getTime() : 0;
      }
      
      // 숫자 필드 처리
      if (column === 'todo_id') {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      }
      
      // 문자열 비교
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (aValue < bValue) return direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return direction === 'asc' ? 1 : -1;
      return 0;
    });
    
    return sorted;
  }, [todos, sortConfig, isMobileView, mobileSortOption]);

  // 정렬 핸들러
  const handleSort = (column) => {
    setSortConfig(prevConfig => {
      if (prevConfig.column === column) {
        return {
          column,
          direction: prevConfig.direction === 'asc' ? 'desc' : 'asc'
        };
      } else {
        return {
          column,
          direction: 'desc'
        };
      }
    });
  };


  const handleCellClick = (rowId, field, value) => {
    // 편집 가능한 필드만 허용
    const editableFields = ['note_1', 'note_2'];
    if (!editableFields.includes(field)) {
      return; // 편집 불가능한 필드는 무시
    }
    
    // 편집 모드일 때만 셀 편집 가능
    if (editingRowId === rowId) {
      setEditingCell({ rowId, field });
      setEditValue(value || '');
    }
  };

  const handleCellBlur = () => {
    if (!editingCell) return;
    
    const { rowId, field } = editingCell;
    const row = todos.find(r => r.todo_id === rowId);
    
    if (!row || row[field] === editValue) {
      setEditingCell(null);
      return;
    }

    // 편집 모드일 때는 행 상태만 업데이트 (저장 버튼을 눌러야 DB에 반영)
    setTodos(prev => prev.map(r => r.todo_id === rowId ? { ...r, [field]: editValue } : r));
    setEditingCell(null);
  };

  // 다중 행 입력: 행 추가
  const handleAddRow = () => {
    const newRowId = Date.now();
    setInputRows(prev => [
      ...prev,
      {
        id: newRowId,
        company_code: '',
        contract_id: '',
        work_description: '',
        note_1: '',
        job_category: '', // 업무 구분 추가
      }
    ]);
    return newRowId;
  };

  // 다중 행 입력: 선택된 행 삭제
  const handleDeleteSelectedRows = () => {
    if (selectedRowIds.length === 0) {
      alert('삭제할 행을 선택해주세요.');
      return;
    }
    
    // 전체 선택 후 삭제하는 경우: 모든 행 삭제 후 빈 행 1개 자동 생성
    if (inputRows.length === selectedRowIds.length) {
      setDeleteConfirmMessage('모든 행을 삭제하시겠습니까? 빈 행 1개가 자동으로 생성됩니다.');
      setDeleteConfirmCallback(() => () => {
        setInputRows([
          {
            id: Date.now(),
            company_code: '',
            contract_id: '',
            work_description: '',
            note_1: '',
            job_category: '', // 업무 구분 추가
          }
        ]);
        setSelectedRowIds([]);
      });
      setDeleteConfirmOpen(true);
      return;
    }
    
    // 일부 행만 선택한 경우
    if (inputRows.length - selectedRowIds.length < 1) {
      alert('최소 1개의 행은 유지해야 합니다.');
      return;
    }
    
    setDeleteConfirmMessage(`선택한 ${selectedRowIds.length}개의 행을 삭제하시겠습니까?`);
    setDeleteConfirmCallback(() => () => {
      setInputRows(prev => prev.filter(row => !selectedRowIds.includes(row.id)));
      setSelectedRowIds([]);
    });
    setDeleteConfirmOpen(true);
  };
  
  // 삭제 확인 모달 핸들러
  const handleDeleteConfirm = () => {
    if (deleteConfirmCallback) {
      deleteConfirmCallback();
    }
    setDeleteConfirmOpen(false);
    setDeleteConfirmCallback(null);
    setDeleteConfirmMessage('');
  };
  
  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setDeleteConfirmCallback(null);
    setDeleteConfirmMessage('');
  };

  // 체크박스 선택/해제
  const handleRowSelect = (rowId) => {
    setSelectedRowIds(prev => 
      prev.includes(rowId) 
        ? prev.filter(id => id !== rowId)
        : [...prev, rowId]
    );
  };

  // 전체 선택/해제
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedRowIds(inputRows.map(row => row.id));
    } else {
      setSelectedRowIds([]);
    }
  };

  // 다중 행 입력: 행 데이터 업데이트
  const handleRowChange = (rowId, field, value) => {
    setInputRows(prev => prev.map(row => 
      row.id === rowId ? { ...row, [field]: value } : row
    ));
  };

  // 다중 행 입력: 계약 선택
  const handleRowContractSelect = (rowId, contract) => {
    setInputRows(prev => prev.map(row => 
      row.id === rowId ? { 
        ...row, 
        contract,
        company_code: contract?.company_code || '',
        contract_id: contract?.contract_id || '',
      } : row
    ));
  };

  // 다중 행 입력: 업무 상세내용 필드 추가 (work_description)
  const handleRowWorkDescriptionChange = (rowId, value) => {
    setInputRows(prev => prev.map(row => 
      row.id === rowId ? { ...row, work_description: value } : row
    ));
  };

  // 다중 행 입력: 일괄 저장
  const handleBatchSubmit = async () => {
    console.log('[handleBatchSubmit] ===== 시작 =====');
    console.log('[handleBatchSubmit] inputRows:', inputRows);
    console.log('[handleBatchSubmit] inputRows 개수:', inputRows.length);
    
    // 유효성 검사: 업체/계약과 업무 상세내용(work_description)이 필수
    const validRows = inputRows.filter(row => {
      const isValid = row.contract && row.work_description;
      console.log('[handleBatchSubmit] 행 검증:', {
        hasContract: !!row.contract,
        hasWorkDescription: !!row.work_description,
        contract: row.contract,
        work_description: row.work_description,
        isValid
      });
      return isValid;
    });

    console.log('[handleBatchSubmit] validRows 개수:', validRows.length);
    console.log('[handleBatchSubmit] validRows:', validRows);

    if (validRows.length === 0) {
      console.warn('[handleBatchSubmit] ❌ 저장할 데이터가 없습니다.');
      setInfoModalTitle('알림');
      setInfoModalMessage('저장할 데이터가 없습니다.\n업체/계약과 업무 상세내용을 입력해주세요.');
      setInfoModalOpen(true);
      return;
    }

    if (validRows.length !== inputRows.length) {
      const invalidCount = inputRows.length - validRows.length;
      if (!window.confirm(`${invalidCount}개의 행이 필수 항목을 입력하지 않아 제외됩니다. 계속하시겠습니까?`)) {
        return;
      }
    }

    try {
      // 일괄 저장 API 호출
      const promises = validRows.map((row, index) => {
        const requestData = {
          company_code: row.contract?.company_code || null,
          contract_id: row.contract?.contract_id || null,
          job_category: row.job_category?.trim() || null, // 업무 구분 추가
          note_1: row.work_description || null, // 업무 상세내용을 note_1로 저장
          note_2: row.note_1 || null, // 비고를 note_2로 저장
          status: 'active',
        };
        
        // 디버깅: 첫 번째 행의 job_category 확인
        if (index === 0) {
          console.log('[handleBatchSubmit] ===== 첫 번째 행 데이터 확인 =====');
          console.log('[handleBatchSubmit] row 객체:', row);
          console.log('[handleBatchSubmit] row.job_category 원본:', row.job_category, '(타입:', typeof row.job_category, ')');
          console.log('[handleBatchSubmit] row.job_category?.trim():', row.job_category?.trim());
          console.log('[handleBatchSubmit] 최종 requestData:', requestData);
          console.log('[handleBatchSubmit] requestData.job_category:', requestData.job_category, '(타입:', typeof requestData.job_category, ')');
          console.log('[handleBatchSubmit] ====================================');
        }
        
        // Zod 검증 적용
        const validation = validateData(TodoCreateSchema, requestData);
        
        if (!validation.success) {
          console.error('[handleBatchSubmit] ❌ Zod 검증 실패:', validation.error);
          setInfoModalTitle('입력 오류');
          setInfoModalMessage(validation.error);
          setInfoModalOpen(true);
          throw new Error(validation.error);
        }
        
        // 검증된 데이터 사용
        const validatedData = validation.data;
        console.log('[handleBatchSubmit] ✅ Zod 검증 통과, 검증된 데이터:', validatedData);
        
        return axios.post(`${API_BASE_URL}/todos`, validatedData);
      });
      
      await Promise.all(promises);
      
      // 성공 후 초기화
      setInputRows([
        {
          id: Date.now(),
          company_code: '',
          contract_id: '',
          work_description: '',
          note_1: '',
          job_category: '', // 업무 구분 추가
        }
      ]);
      setSelectedRowIds([]);
      
      // 최근 계약 목록 갱신
      fetchRecentContracts();
      
      // 목록 새로고침
      fetchTodos();
      
      // RightSidebar 갱신을 위한 이벤트 발생
      window.dispatchEvent(new CustomEvent('todoListUpdated'));
      
      // 성공 메시지
      setInfoModalTitle('저장 완료');
      setInfoModalMessage(`${validRows.length}개의 To-Do가 등록되었습니다.`);
      setInfoModalOpen(true);
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || '저장 실패';
      setInfoModalTitle('오류');
      setInfoModalMessage(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
      setInfoModalOpen(true);
    }
  };

  // 빠른 추가 (모바일용) - 최소 필드로 최적화
  const handleAddRowMobile = () => {
    setEditingTodo(null);
    setBottomSheetOpen(true);
    // 자동 포커스는 TodoBottomSheet에서 처리
  };

  const handleEditTodo = (todo) => {
    setEditingTodo(todo);
    setBottomSheetOpen(true);
  };

  // 인라인 편집: 편집 모드 시작
  const handleEditClick = (rowId) => {
    setEditingRowId(rowId);
    setEditingCell(null);
  };

  // 더블클릭 또는 F2로 수정모드 진입
  const handleRowDoubleClick = (rowId) => {
    setEditingRowId(rowId);
    // 첫 번째 편집 가능한 필드(업무내용 상세)로 자동 진입
    setTimeout(() => {
      const row = todos.find(r => r.todo_id === rowId);
      if (row) {
        handleCellClick(rowId, 'note_1', row.note_1);
      }
    }, 50);
  };

  // F2 키로 편집 모드 진입 (페이지 레벨)
  useEffect(() => {
    const handleKeyDown = (e) => {
      // 모달이 열려 있으면 무시
      if (bottomSheetOpen) {
        return;
      }
      
      if (e.key === 'F2') {
        e.preventDefault();
        
        // 이미 편집 모드인 경우: 첫 번째 셀에 커서 활성화
        if (editingRowId) {
          const row = todos.find(r => r.todo_id === editingRowId);
          if (row) {
            // 첫 번째 편집 가능한 필드로 진입
            handleCellClick(editingRowId, 'note_1', row.note_1);
          }
          return;
        }
        
        // 편집 모드가 아닌 경우: 행을 편집 모드로 전환
        // 우선순위 1: 포커스된 행이 있으면 해당 행 편집
        const focusedRow = document.activeElement.closest('[data-row-id]');
        if (focusedRow) {
          const rowId = parseInt(focusedRow.getAttribute('data-row-id'), 10);
          if (rowId && todos.find(r => r.todo_id === rowId)) {
            setEditingRowId(rowId);
            return;
          }
        }
        
        // 우선순위 2: 첫 번째 To-Do가 있으면 편집 모드로
        if (todos.length > 0) {
          setEditingRowId(todos[0].todo_id);
        }
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editingRowId, bottomSheetOpen, todos]);

  // 탭 키로 다음 편집 가능한 필드로 이동
  const handleFieldTab = (rowId, currentField) => {
    const editableFields = ['note_1', 'note_2'];
    const currentIndex = editableFields.indexOf(currentField);
    
    if (currentIndex < editableFields.length - 1) {
      // 다음 필드로 이동
      const nextField = editableFields[currentIndex + 1];
      const row = todos.find(r => r.todo_id === rowId);
      if (row) {
        handleCellClick(rowId, nextField, row[nextField]);
      }
    } else {
      // 마지막 필드면 저장
      handleSaveClick(rowId);
    }
  };

  // 인라인 편집: 저장
  const handleSaveClick = async (rowId) => {
    let row = todos.find(r => r.todo_id === rowId);
    if (!row) return;

    // 편집 중인 셀이 있으면 editValue를 먼저 반영
    if (editingCell && editingCell.rowId === rowId) {
      const { field } = editingCell;
      row = { ...row, [field]: editValue };
      
      // todos 상태 업데이트
      setTodos(prev => prev.map(r => 
        r.todo_id === rowId ? row : r
      ));
      
      // 편집 모드 해제
      setEditingCell(null);
    }

    try {
      // 백엔드 API가 기대하는 형식으로 데이터 구성
      const updateData = {
        company_code: row.company_code || null,
        contract_id: row.contract_id || null,
        job_category: row.job_category?.trim() || null, // 업무 구분 추가
        note_1: row.note_1 || null,
        note_2: row.note_2 || null,
        status: row.status || 'active',
      };
      
      // Zod 검증 적용
      const validation = validateData(TodoUpdateSchema, updateData);
      
      if (!validation.success) {
        console.error('[handleSaveClick] ❌ Zod 검증 실패:', validation.error);
        setSnackbarMessage(`입력 오류: ${validation.error}`);
        setSnackbarOpen(true);
        return;
      }
      
      // 검증된 데이터 사용
      const validatedData = validation.data;
      console.log('[handleSaveClick] ✅ Zod 검증 통과, 검증된 데이터:', validatedData);
      
      await axios.put(`${API_BASE_URL}/todos/${rowId}`, validatedData);
      setEditingRowId(null);
      setEditingCell(null);
      await fetchTodos();
      // RightSidebar 갱신을 위한 이벤트 발생
      window.dispatchEvent(new CustomEvent('todoListUpdated'));
      // alert 제거 (성공 시 알람 없음)
    } catch (error) {
      console.error('저장 실패:', error);
      console.error('에러 상세:', error.response?.data);
      alert(error.response?.data?.detail || '저장 실패');
    }
  };

  // 인라인 편집: 취소
  const handleCancelEdit = () => {
    setEditingRowId(null);
    setEditingCell(null);
    // 변경사항 되돌리기 위해 목록 새로고침
    fetchTodos();
  };

  const handleSubmitTodo = async (data) => {
    try {
      const requestData = {
        company_code: data.company_code || null,
        contract_id: data.contract_id || null,
        job_category: data.job_category?.trim() || null, // 업무 구분 추가
        note_1: data.note_1 || null, // 업무 상세내용을 note_1로 저장
        note_2: data.note_2 || null, // 비고를 note_2로 저장
        status: editingTodo ? (editingTodo.status || 'active') : 'active',
      };
      
      // Zod 검증 적용
      const schema = editingTodo ? TodoUpdateSchema : TodoCreateSchema;
      const validation = validateData(schema, requestData);
      
      if (!validation.success) {
        console.error('[handleSubmitTodo] ❌ Zod 검증 실패:', validation.error);
        alert(validation.error);
        return;
      }
      
      // 검증된 데이터 사용
      const validatedData = validation.data;
      console.log('[handleSubmitTodo] ✅ Zod 검증 통과, 검증된 데이터:', validatedData);
      
      if (editingTodo) {
        // 수정 - PUT /todos/{id} 엔드포인트 확인 필요
        await axios.put(`${API_BASE_URL}/todos/${editingTodo.todo_id}`, validatedData);
        setSnackbarMessage('To-Do가 수정되었습니다.');
      } else {
        // 추가
        await axios.post(`${API_BASE_URL}/todos`, validatedData);
        setSnackbarMessage('To-Do가 추가되었습니다.');
      }
      setBottomSheetOpen(false);
      setEditingTodo(null);
      setSnackbarOpen(true);
      fetchTodos();
      
      // RightSidebar 갱신을 위한 이벤트 발생
      window.dispatchEvent(new CustomEvent('todoListUpdated'));
    } catch (error) {
      const errorMessage = error.response?.data?.detail || error.message || (editingTodo ? '수정 실패' : '추가 실패');
      console.error('To-Do 저장 실패:', error);
      console.error('에러 상세:', error.response?.data);
      alert(errorMessage);
    }
  };


  const handleToggleStatus = async (todoId, newStatus) => {
    // 드롭다운에서 선택한 상태값을 그대로 사용
    const status = newStatus === 'active' || newStatus === 'completed' ? newStatus : 'active';
    console.log('[handleToggleStatus] 상태 변경 시작:', { todoId, newStatus, status });
    
    // 즉시 로컬 상태 업데이트 (낙관적 업데이트) - 체크박스가 즉시 반응하도록
    setTodos(prevTodos => 
      prevTodos.map(todo => 
        todo.todo_id === todoId 
          ? { ...todo, status: status }
          : todo
      )
    );
    
    try {
      // DB에 상태 업데이트
      const response = await axios.put(`${API_BASE_URL}/todos/${todoId}/status`, { status });
      console.log('[handleToggleStatus] 상태 변경 성공:', response.data);
      // 목록 새로고침 (서버 상태와 동기화)
      await fetchTodos();
      // RightSidebar 갱신을 위한 이벤트 발생
      window.dispatchEvent(new CustomEvent('todoListUpdated'));
    } catch (error) {
      console.error('[handleToggleStatus] 상태 변경 실패:', error);
      // 실패 시 원래 상태로 롤백
      setTodos(prevTodos => 
        prevTodos.map(todo => 
          todo.todo_id === todoId 
            ? { ...todo, status: newStatus === 'completed' ? 'active' : 'completed' }
            : todo
        )
      );
      const errorMessage = error.response?.data?.detail 
        ? (typeof error.response.data.detail === 'string' 
          ? error.response.data.detail 
          : JSON.stringify(error.response.data.detail))
        : error.response?.data?.message || error.message || '상태 변경 실패';
      alert(errorMessage);
    }
  };

  const handleDelete = async (todoId) => {
    console.log('[handleDelete] 삭제 요청:', { todoId });
    const todo = todos.find(t => t.todo_id === todoId);
    if (!todo) {
      console.warn('[handleDelete] To-Do를 찾을 수 없음:', todoId);
      return;
    }
    
    setDeleteConfirmMessage(`To-Do 항목을 삭제하시겠습니까?`);
    setDeleteConfirmCallback(() => async () => {
      const todoToRestore = { ...todo };
      try {
        console.log('[handleDelete] 삭제 실행:', { todoId: todo.todo_id });
        await axios.delete(`${API_BASE_URL}/todos/${todo.todo_id}`);
        setDeletedTodo(todoToRestore);
        fetchTodos();
        // RightSidebar 갱신을 위한 이벤트 발생
        window.dispatchEvent(new CustomEvent('todoListUpdated'));
        setSnackbarMessage('항목이 삭제되었습니다.');
        setSnackbarOpen(true);
      } catch (error) {
        console.error('[handleDelete] 삭제 실패:', error);
        alert(error.response?.data?.detail || '삭제 실패');
      }
    });
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!todoToDelete) return;
    const todoToRestore = { ...todoToDelete };
    try {
      await axios.delete(`${API_BASE_URL}/todos/${todoToDelete.todo_id}`);
      setDeletedTodo(todoToRestore);
      setDeleteConfirmOpen(false);
      setTodoToDelete(null);
      fetchTodos();
      setSnackbarMessage('항목이 삭제되었습니다.');
      setSnackbarOpen(true);
    } catch (error) {
      alert(error.response?.data?.detail || '삭제 실패');
    }
  };

  const handleUndoDelete = async () => {
    if (!deletedTodo) return;
    try {
      await axios.post(`${API_BASE_URL}/todos`, {
        company_code: deletedTodo.company_code,
        contract_id: deletedTodo.contract_id,
        note_1: deletedTodo.note_1,
        status: deletedTodo.status,
      });
      setDeletedTodo(null);
      fetchTodos();
      setSnackbarMessage('항목이 복구되었습니다.');
      setSnackbarOpen(true);
    } catch (error) {
      alert(error.response?.data?.detail || '복구 실패');
    }
  };


  const handleCreateLog = async () => {
    if (!selectedTodoForLog) return;
    try {
      await axios.post(`${API_BASE_URL}/todos/${selectedTodoForLog.todo_id}/log`, logFormData);
      setLogDialogOpen(false);
      setSelectedTodoForLog(null);
      alert('투입시간이 반영되었습니다.');
    } catch (error) {
      alert(error.response?.data?.detail || '투입시간 반영 실패');
    }
  };


  const handleRowHeightChange = useCallback((height, rowId) => {
    setRowHeights(prev => ({ ...prev, [rowId]: height }));
  }, []);

  // 파일 업로드 처리
  const handleFileUpload = async (file) => {
    try {
      const data = await readExcelFile(file);
      if (data && data.length > 0) {
        // 엑셀 데이터를 todos 형식으로 변환
        const convertedData = data.map((row, index) => ({
          todo_id: `UPLOAD_${Date.now()}_${index}`,
          company_code: row['업체코드'] || row['company_code'] || '',
          contract_id: row['계약번호'] || row['contract_id'] || '',
          status: row['진행상태'] || row['status'] || 'active',
          note_1: row['업무 상세내용'] || row['note_1'] || '',
        }));
        setTodos(prev => [...prev, ...convertedData]);
        alert(`${convertedData.length}개의 데이터가 불러와졌습니다.`);
      }
    } catch (error) {
      alert(`파일 읽기 실패: ${error.message}`);
    }
  };


  // 엑셀 다운로드 함수
  const handleDownloadExcel = () => {
    if (sortedTodos.length === 0) {
      alert('다운로드할 데이터가 없습니다.');
      return;
    }

    // 엑셀 데이터 준비
    const excelData = sortedTodos.map((todo, index) => {
      const company = companies.find(c => c.company_code === todo.company_code);
      const contract = contracts.find(c => c.contract_id === todo.contract_id);
      return {
        '번호': index + 1,
        'ID': todo.todo_id,
        '업체코드': todo.company_code || '',
        '업체명': company?.company_name_kr || company?.company_name || '',
        '계약번호': todo.contract_id || '',
        '계약명': contract?.contract_title || '',
        '업무 구분': todo.job_category || '',
        '업무 상세내용': todo.note_1 || '',
        '진행경과': todo.status === 'completed' ? '완료' : '진행중',
        '생성일시': todo.created_at ? format(new Date(todo.created_at), 'yyyy-MM-dd HH:mm') : '',
        '수정일시': todo.updated_at ? format(new Date(todo.updated_at), 'yyyy-MM-dd HH:mm') : '',
      };
    });

    // 워크북 생성
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(excelData);
    
    // 컬럼 너비 설정
    const colWidths = [
      { wch: 8 },   // 번호
      { wch: 10 },  // ID
      { wch: 15 },  // 업체코드
      { wch: 20 },  // 업체명
      { wch: 18 },  // 계약번호
      { wch: 25 },  // 계약명
      { wch: 15 },  // 업무 구분
      { wch: 30 },  // 업무 상세내용
      { wch: 12 },  // 진행경과
      { wch: 18 },  // 생성일시
      { wch: 18 }   // 수정일시
    ];
    ws['!cols'] = colWidths;
    
    XLSX.utils.book_append_sheet(wb, ws, 'To-Do 리스트');
    
    // 파일명 생성
    const employeeName = localStorage.getItem('employeeName') || '사용자';
    const now = new Date();
    const dateStr = format(now, 'yyyyMMdd');
    const timeStr = format(now, 'HHmmss');
    const fileName = `OnTimeLog_${employeeName}_ToDo_${dateStr}_${timeStr}.xlsx`;
    
    // 파일 다운로드
    XLSX.writeFile(wb, fileName);
  };


  const renderEditableCell = (row, field, value) => {
    const isEditing = editingCell?.rowId === row.todo_id && editingCell?.field === field;
    const isRowEditing = editingRowId === row.todo_id;
    
    // 편집 가능한 필드 체크
    const editableFields = ['note_1', 'note_2'];
    const isFieldEditable = editableFields.includes(field);
    
    // 수정 모드가 아니거나 편집 불가능한 필드면 텍스트만 표시
    if (!isRowEditing || !isFieldEditable) {
      return (
        <Box
          sx={{
            width: '100%',
            minHeight: '32px',
            padding: '8px',
            textDecoration: row.status === 'completed' ? 'line-through' : 'none',
            opacity: row.status === 'completed' ? 0.6 : 1,
          }}
        >
          {value || '-'}
        </Box>
      );
    }
    
    if (isEditing) {
      return (
        <TextField
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleCellBlur}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              // Shift+Enter는 줄바꿈, Enter만 누르면 저장
              e.preventDefault();
              handleCellBlur();
              // 값 반영 후 저장
              setTimeout(() => handleSaveClick(row.todo_id), 50);
            }
            if (e.key === 'Tab') {
              e.preventDefault();
              handleCellBlur();
              // 값 반영 후 다음 필드로 이동
              setTimeout(() => handleFieldTab(row.todo_id, field), 50);
            }
            if (e.key === 'Escape') setEditingCell(null);
          }}
          autoFocus
          size="small"
          fullWidth
          multiline={field === 'note_1' || field === 'note_2'}
          rows={field === 'note_1' || field === 'note_2' ? 2 : 1}
          sx={{ fontSize: '14px', '& .MuiInputBase-input': { fontSize: '14px' } }}
        />
      );
    }
    
    // 편집 가능한 필드만 클릭 가능 (이미 위에서 선언된 editableFields 재사용)
    const canEdit = isRowEditing && isFieldEditable;
    
    return (
      <Box
        onClick={() => canEdit && handleCellClick(row.todo_id, field, value)}
        sx={{
          width: '100%',
          minHeight: '32px',
          padding: '8px',
          cursor: canEdit ? 'pointer' : 'default',
          borderRadius: '4px',
          textDecoration: row.status === 'completed' ? 'line-through' : 'none',
          opacity: row.status === 'completed' ? 0.6 : 1,
          // 편집 가능한 필드에 음영 효과 추가
          backgroundColor: canEdit 
            ? (isDark ? 'rgba(138,124,255,0.1)' : 'rgba(138,124,255,0.05)')
            : 'transparent',
          border: canEdit 
            ? `1px dashed ${colors.accent}` 
            : '1px solid transparent',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: canEdit 
              ? (isDark ? 'rgba(138,124,255,0.15)' : 'rgba(138,124,255,0.1)')
              : 'transparent',
          },
        }}
      >
        {value || '-'}
      </Box>
    );
  };

  // 정렬된 todos 사용 (allRows는 더 이상 사용하지 않음)
  // '완료 포함' 체크박스가 체크되어 있으면 completed 상태도 포함
  // 날짜 범위 필터링 적용
  const filteredTodos = includeCompleted 
    ? sortedTodos // 완료 포함이면 모든 항목
    : sortedTodos.filter(t => t.status === 'active'); // 완료 포함이 아니면 active만
  
  // 날짜 증감 유틸리티
  const adjustDateByDays = (dateStr, delta) => {
    if (!dateStr) {
      const today = new Date();
      today.setDate(today.getDate() + delta);
      return format(today, 'yyyy-MM-dd');
    }
    const baseDate = new Date(dateStr);
    if (Number.isNaN(baseDate.getTime())) {
      const today = new Date();
      today.setDate(today.getDate() + delta);
      return format(today, 'yyyy-MM-dd');
    }
    baseDate.setDate(baseDate.getDate() + delta);
    return format(baseDate, 'yyyy-MM-dd');
  };

  const handleRangeDateStep = (target, delta) => {
    const currentDate = target === 'start' ? dateRange.startDate : dateRange.endDate;
    const newDate = adjustDateByDays(currentDate || (target === 'start' ? getDefaultStartDate() : getDefaultEndDate()), delta);
    if (target === 'start') {
      setDateRange(prev => ({ ...prev, startDate: newDate }));
    } else {
      setDateRange(prev => ({ ...prev, endDate: newDate }));
    }
  };

  // 투입시간 반영 다이얼로그의 업무일자 증감 핸들러
  const handleLogDateStep = (delta) => {
    const newDate = adjustDateByDays(logFormData.log_date, delta);
    setLogFormData({ ...logFormData, log_date: newDate });
  };

  // 날짜 범위 필터링
  const activeTodos = useMemo(() => {
    return filteredTodos.filter(todo => {
      // 날짜 범위가 설정되지 않으면 모든 항목 표시 (null 또는 빈 문자열 체크)
      const hasStartDate = dateRange.startDate && dateRange.startDate.trim() !== '';
      const hasEndDate = dateRange.endDate && dateRange.endDate.trim() !== '';
      
      if (!hasStartDate && !hasEndDate) {
        return true;
      }
      
      // created_at이 없으면 제외
      if (!todo.created_at) {
        return false;
      }
      
      // 날짜 문자열을 yyyy-MM-dd 형식으로 추출
      const getDateString = (dateValue) => {
        if (!dateValue || (typeof dateValue === 'string' && dateValue.trim() === '')) {
          return null;
        }
        // 이미 yyyy-MM-dd 형식이면 그대로 사용
        if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue.trim())) {
          return dateValue.trim();
        }
        // Date 객체나 ISO 문자열이면 yyyy-MM-dd로 변환
        try {
          const date = new Date(dateValue);
          if (isNaN(date.getTime())) return null;
          return format(date, 'yyyy-MM-dd');
        } catch (error) {
          return null;
        }
      };
      
      // todo의 created_at을 yyyy-MM-dd 형식으로 변환
      const todoDateStr = getDateString(todo.created_at);
      if (!todoDateStr) {
        return false; // 날짜를 파싱할 수 없으면 제외
      }
      
      const startDateStr = hasStartDate ? getDateString(dateRange.startDate) : null;
      const endDateStr = hasEndDate ? getDateString(dateRange.endDate) : null;
      
      // 날짜 문자열로 직접 비교 (타임존 문제 방지)
      if (startDateStr && endDateStr) {
        return todoDateStr >= startDateStr && todoDateStr <= endDateStr;
      } else if (startDateStr) {
        return todoDateStr >= startDateStr;
      } else if (endDateStr) {
        return todoDateStr <= endDateStr;
      }
      
      return true;
    });
  }, [filteredTodos, dateRange.startDate, dateRange.endDate]);

  // 날짜 기준 그룹화 함수
  const groupTodosByDate = (todoList) => {
    const today = startOfDay(new Date());
    const tomorrow = startOfDay(addDays(today, 1));
    const nextWeek = startOfDay(addDays(today, 7));

    const groups = {
      today: [],
      tomorrow: [],
      nextWeek: [],
      later: [],
      noDate: [],
    };

    todoList.forEach(todo => {
      // due_date가 없으면 created_at을 기준으로 사용
      const dateStr = todo.due_date || todo.created_at;
      if (!dateStr) {
        groups.noDate.push(todo);
        return;
      }

      const todoDate = startOfDay(new Date(dateStr));
      const daysDiff = differenceInDays(todoDate, today);

      if (daysDiff < 0) {
        // 과거 날짜는 "나중에" 그룹에 포함
        groups.later.push(todo);
      } else if (isToday(todoDate)) {
        groups.today.push(todo);
      } else if (isTomorrow(todoDate)) {
        groups.tomorrow.push(todo);
      } else if (daysDiff <= 7) {
        groups.nextWeek.push(todo);
      } else {
        groups.later.push(todo);
      }
    });

    return groups;
  };

  // 디버깅: 모바일 뷰 감지 상태 확인
  React.useEffect(() => {
    console.log('[TodoList] isMobileView:', isMobileView);
    console.log('[TodoList] body.classList:', document.body.classList.toString());
  }, [isMobileView]);

  // 모바일 뷰 렌더링
  if (isMobileView) {
    return (
      <Box
        className="mobile-content"
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: colors.primaryBg,
          position: 'relative',
          minHeight: 'calc(100vh - 72px - 96px)', // 전체 높이 - TopBar(72px) - BottomNav(96px)
          pb: 12, // 하단 네비게이션 공간 (더 여유있게)
        }}
      >
        {/* 헤더 */}
        <Box
          sx={{
            pt: 4,
            px: 3,
            pb: 2,
            borderBottom: `1px solid ${colors.primaryBorder}`,
            backgroundColor: isDark ? '#1C1D22' : '#FFFFFF',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography 
              variant="h6" 
              sx={{ 
                color: colors.primaryText, 
                fontWeight: 600, 
                fontSize: '16px',
                lineHeight: 1.4,
              }}
            >
              To-Do 리스트
            </Typography>
          </Box>
          {/* 완료 포함 체크박스와 기간 선택 배지를 동일 선상에 배치 */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1 }}>
            {/* 완료 포함 체크박스 */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeCompleted}
                  onChange={(e) => setIncludeCompleted(e.target.checked)}
                  size="small"
                  sx={{
                    '& .MuiSvgIcon-root': {
                      fontSize: '20px',
                    },
                  }}
                />
              }
              label={
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: colors.primaryText,
                    fontSize: '14px',
                    fontWeight: 500,
                  }}
                >
                  완료 포함
                </Typography>
              }
              sx={{
                margin: 0,
                '& .MuiFormControlLabel-label': {
                  marginLeft: 1,
                },
              }}
            />
            {/* 빠른 선택 배지 */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip
                label="일주일"
                onClick={() => {
                  const today = new Date();
                  const weekStart = startOfWeek(today, { weekStartsOn: 1 });
                  const weekEnd = endOfWeek(today, { weekStartsOn: 1 });
                  setDateRange({
                    startDate: format(weekStart, 'yyyy-MM-dd'),
                    endDate: format(weekEnd, 'yyyy-MM-dd'),
                  });
                }}
                size="small"
                sx={{
                  fontSize: '12px',
                  height: '28px',
                  cursor: 'pointer',
                  backgroundColor: dateRange.startDate && dateRange.endDate && 
                    format(new Date(dateRange.startDate), 'yyyy-MM-dd') === format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd') &&
                    format(new Date(dateRange.endDate), 'yyyy-MM-dd') === format(endOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd')
                    ? colors.accent
                    : isDark ? '#2A2B30' : '#F0F0F0',
                  color: dateRange.startDate && dateRange.endDate && 
                    format(new Date(dateRange.startDate), 'yyyy-MM-dd') === format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd') &&
                    format(new Date(dateRange.endDate), 'yyyy-MM-dd') === format(endOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd')
                    ? '#FFFFFF'
                    : colors.primaryText,
                  '&:hover': {
                    backgroundColor: colors.accent,
                    color: '#FFFFFF',
                  },
                }}
              />
              <Chip
                label="한달"
                onClick={() => {
                  const today = new Date();
                  const monthStart = startOfMonth(today);
                  const monthEnd = endOfMonth(today);
                  setDateRange({
                    startDate: format(monthStart, 'yyyy-MM-dd'),
                    endDate: format(monthEnd, 'yyyy-MM-dd'),
                  });
                }}
                size="small"
                sx={{
                  fontSize: '12px',
                  height: '28px',
                  cursor: 'pointer',
                  backgroundColor: dateRange.startDate && dateRange.endDate && 
                    format(new Date(dateRange.startDate), 'yyyy-MM-dd') === format(startOfMonth(new Date()), 'yyyy-MM-dd') &&
                    format(new Date(dateRange.endDate), 'yyyy-MM-dd') === format(endOfMonth(new Date()), 'yyyy-MM-dd')
                    ? colors.accent
                    : isDark ? '#2A2B30' : '#F0F0F0',
                  color: dateRange.startDate && dateRange.endDate && 
                    format(new Date(dateRange.startDate), 'yyyy-MM-dd') === format(startOfMonth(new Date()), 'yyyy-MM-dd') &&
                    format(new Date(dateRange.endDate), 'yyyy-MM-dd') === format(endOfMonth(new Date()), 'yyyy-MM-dd')
                    ? '#FFFFFF'
                    : colors.primaryText,
                  '&:hover': {
                    backgroundColor: colors.accent,
                    color: '#FFFFFF',
                  },
                }}
              />
              <Chip
                label="전체"
                onClick={() => {
                  setDateRange({ startDate: null, endDate: null });
                }}
                size="small"
                sx={{
                  fontSize: '12px',
                  height: '28px',
                  cursor: 'pointer',
                  backgroundColor: !dateRange.startDate && !dateRange.endDate
                    ? colors.accent
                    : isDark ? '#2A2B30' : '#F0F0F0',
                  color: !dateRange.startDate && !dateRange.endDate
                    ? '#FFFFFF'
                    : colors.primaryText,
                  '&:hover': {
                    backgroundColor: colors.accent,
                    color: '#FFFFFF',
                  },
                }}
              />
            </Box>
          </Box>
          {/* 배지 시스템 */}
          <TodoBadgeSystem />
          {/* 조회 기간 선택 */}
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* 시작일~종료일 선택 */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                type="date"
                label="시작일"
                value={dateRange.startDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment 
                      position="end" 
                      sx={{ 
                        mr: -0.5,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRangeDateStep('start', 1);
                          }}
                          sx={{ 
                            p: 0.25, 
                            color: colors.secondaryText,
                            minWidth: '20px',
                            width: '20px',
                            height: '16px',
                          }}
                          aria-label="시작일 하루 증가"
                        >
                          <ChevronUp size={14} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRangeDateStep('start', -1);
                          }}
                          sx={{ 
                            p: 0.25, 
                            color: colors.secondaryText,
                            minWidth: '20px',
                            width: '20px',
                            height: '16px',
                          }}
                          aria-label="시작일 하루 감소"
                        >
                          <ChevronDown size={14} />
                        </IconButton>
                      </Box>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  minWidth: 200, // 최소 너비 보장
                  '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                    filter: isDark ? 'invert(1)' : 'none',
                    opacity: 1,
                    cursor: 'pointer',
                  },
                  '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator:hover': {
                    opacity: 0.7,
                  },
                }}
                inputProps={{
                  sx: {
                    color: colors.primaryText,
                    '&::-webkit-calendar-picker-indicator': {
                      filter: isDark ? 'invert(1)' : 'none',
                    },
                  }
                }}
              />
              <Typography variant="body2" sx={{ color: colors.secondaryText, fontSize: '14px' }}>
                ~
              </Typography>
              <TextField
                type="date"
                label="종료일"
                value={dateRange.endDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment 
                      position="end" 
                      sx={{ 
                        mr: -0.5,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRangeDateStep('end', 1);
                          }}
                          sx={{ 
                            p: 0.25, 
                            color: colors.secondaryText,
                            minWidth: '20px',
                            width: '20px',
                            height: '16px',
                          }}
                          aria-label="종료일 하루 증가"
                        >
                          <ChevronUp size={14} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRangeDateStep('end', -1);
                          }}
                          sx={{ 
                            p: 0.25, 
                            color: colors.secondaryText,
                            minWidth: '20px',
                            width: '20px',
                            height: '16px',
                          }}
                          aria-label="종료일 하루 감소"
                        >
                          <ChevronDown size={14} />
                        </IconButton>
                      </Box>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  minWidth: 200, // 최소 너비 보장
                  '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                    filter: isDark ? 'invert(1)' : 'none',
                    opacity: 1,
                    cursor: 'pointer',
                  },
                  '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator:hover': {
                    opacity: 0.7,
                  },
                }}
                inputProps={{
                  sx: {
                    color: colors.primaryText,
                    '&::-webkit-calendar-picker-indicator': {
                      filter: isDark ? 'invert(1)' : 'none',
                    },
                  }
                }}
              />
            </Box>
          </Box>
        </Box>

        {/* 날짜 그룹화된 To-Do 리스트 */}
        {activeTodos.length === 0 ? (
          <Box sx={{ 
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            textAlign: 'center',
            width: '100%',
          }}>
            <CheckSquare size={48} style={{ color: colors.secondaryText, marginBottom: 16 }} />
            <Typography variant="body1" sx={{ color: colors.secondaryText }}>
              등록된 To-Do 계획이 없습니다
            </Typography>
          </Box>
        ) : (
          <Box sx={{ 
            flex: 1, 
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch', // iOS 부드러운 스크롤
            touchAction: 'pan-y', // 수직 스크롤만 허용
            px: 3, 
            pt: 3, // 조건 필터창과 "항목(00개)" 텍스트 사이 공간 여유 추가
            pb: 2, 
            minHeight: 0,
          }}>
            {(() => {
              const groupedTodos = groupTodosByDate(activeTodos);
              const groupLabels = {
              today: `오늘 (${format(new Date(), 'M월 d일', { locale: ko })})`,
              tomorrow: `내일 (${format(addDays(new Date(), 1), 'M월 d일', { locale: ko })})`,
              nextWeek: '다음 7일',
              later: '항목',
              noDate: '기한 없음',
            };

            return (
              <Box>
                {['today', 'tomorrow', 'nextWeek', 'later', 'noDate'].map(groupKey => {
                  const groupTodos = groupedTodos[groupKey];
                  if (groupTodos.length === 0) return null;

                  return (
                    <Box key={groupKey} sx={{ mb: 3 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: colors.secondaryText,
                          fontSize: '12px', // 타이틀 크기 이하로 조정
                          fontWeight: 600,
                          mb: 1.5,
                          px: 1,
                        }}
                      >
                        {groupLabels[groupKey]} ({groupTodos.length}개)
                      </Typography>
                      {groupTodos.map((todo) => (
                        <SwipeableListItem
                          key={todo.todo_id}
                          onSwipeLeft={() => {
                            // 왼쪽으로 스와이프 → 삭제
                            console.log('[onSwipeLeft] 왼쪽 스와이프 감지 - 삭제 실행:', { todoId: todo.todo_id });
                            handleDelete(todo.todo_id);
                          }}
                          onSwipeRight={() => {
                            // 오른쪽으로 스와이프 → 투입시간 반영
                            setSelectedTodoForLog(todo);
                            setLogDialogOpen(true);
                          }}
                          onComplete={() => handleToggleStatus(todo.todo_id, todo.status === 'completed' ? 'active' : 'completed')}
                          onEdit={() => handleEditTodo(todo)}
                          onDelete={() => handleDelete(todo.todo_id)}
                          onConvert={() => {
                            setSelectedTodoForLog(todo);
                            setLogDialogOpen(true);
                          }}
                          leftActions={[
                            {
                              icon: Trash2,
                              label: '삭제',
                              onClick: () => handleDelete(todo.todo_id),
                              color: colors.error || '#f44336',
                            },
                          ]}
                          rightActions={[
                            {
                              icon: CheckCircle,
                              label: '완료',
                              onClick: () => handleToggleStatus(todo.todo_id, todo.status === 'completed' ? 'active' : 'completed'),
                              color: '#4CAF50',
                            },
                            {
                              icon: Clock,
                              label: '투입시간 반영',
                              onClick: () => {
                                setSelectedTodoForLog(todo);
                                setLogDialogOpen(true);
                              },
                              color: colors.accent,
                            },
                          ]}
                          threshold={80}
                          sx={{
                            mb: 1, // 카드 간 간격을 줄여서 가독성 개선
                          }}
                        >
                          <Paper
                            sx={{
                              p: 2.5,
                              backgroundColor: isDark ? '#25262B' : '#FFFFFF',
                              border: `1px solid ${colors.primaryBorder}`,
                              borderRadius: 2,
                              boxShadow: isDark 
                                ? '0 2px 8px rgba(0,0,0,0.2)' 
                                : '0 2px 8px rgba(0,0,0,0.06)',
                              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                              '&:active': {
                                transform: 'scale(0.98)',
                                boxShadow: isDark 
                                  ? '0 1px 4px rgba(0,0,0,0.15)' 
                                  : '0 1px 4px rgba(0,0,0,0.04)',
                              },
                            }}
                          >
                            <Box 
                              sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}
                              onTouchStart={(e) => {
                                // 체크박스 영역의 터치 이벤트 전파 차단
                                e.stopPropagation();
                              }}
                              onTouchMove={(e) => {
                                // 체크박스 영역의 터치 이벤트 전파 차단
                                e.stopPropagation();
                              }}
                              onTouchEnd={(e) => {
                                // 체크박스 영역의 터치 이벤트 전파 차단
                                e.stopPropagation();
                              }}
                              onClick={(e) => {
                                // 체크박스 영역의 클릭 이벤트 전파 차단
                                e.stopPropagation();
                              }}
                            >
                              <Box
                                data-checkbox-container="true"
                                sx={{ 
                                  touchAction: 'manipulation',
                                  pointerEvents: 'auto',
                                }}
                              >
                                <Checkbox
                                  checked={todo.status === 'completed'}
                                  onChange={(e) => {
                                    // 이벤트 전파는 막되, 체크박스의 기본 동작은 허용
                                    e.stopPropagation();
                                    console.log('[Checkbox onChange] 체크박스 클릭:', { 
                                      todoId: todo.todo_id, 
                                      currentStatus: todo.status,
                                      newStatus: todo.status === 'completed' ? 'active' : 'completed',
                                      checked: e.target.checked
                                    });
                                    // 햅틱 피드백 (지원되는 경우)
                                    if (navigator.vibrate) {
                                      navigator.vibrate(10);
                                    }
                                    // 체크박스 클릭 시 상태 토글 (완료/진행중)
                                    const newStatus = todo.status === 'completed' ? 'active' : 'completed';
                                    handleToggleStatus(todo.todo_id, newStatus);
                                  }}
                                  onClick={(e) => {
                                    // 클릭 이벤트 전파만 차단 (체크박스 기본 동작은 허용)
                                    e.stopPropagation();
                                  }}
                                  sx={{ 
                                    mt: 0,
                                    minWidth: '48px',
                                    minHeight: '48px',
                                    padding: '12px',
                                    transition: 'transform 0.15s ease',
                                    touchAction: 'manipulation', // 스와이프와 충돌 방지
                                    pointerEvents: 'auto', // 포인터 이벤트 명시적 활성화
                                    '&:active': {
                                      transform: 'scale(1.1)',
                                    },
                                  }}
                                  aria-label={todo.status === 'completed' ? '완료 취소' : '완료 처리'}
                                />
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    color: colors.primaryText,
                                    fontWeight: 500,
                                    mb: 0.75,
                                    wordBreak: 'break-word',
                                    fontSize: '15px',
                                    lineHeight: 1.5,
                                    textDecoration: todo.status === 'completed' ? 'line-through' : 'none',
                                    opacity: todo.status === 'completed' ? 0.6 : 1,
                                  }}
                                >
                                  {todo.note_1 || '(비고 없음)'}
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                                  {(() => {
                                    // 업체명칭 찾기
                                    const company = todo.company_code 
                                      ? companies.find(c => c.company_code === todo.company_code)
                                      : null;
                                    const companyName = company?.company_name_kr || todo.company_code || '';
                                    
                                    // 계약명칭 찾기
                                    const contract = todo.contract_id
                                      ? contracts.find(c => c.contract_id === todo.contract_id)
                                      : null;
                                    const contractTitle = contract?.contract_title || todo.contract_id || '';
                                    
                                    return (
                                      <>
                                        {companyName && (
                                          <Chip
                                            label={companyName}
                                            size="small"
                                            sx={{ 
                                              fontSize: '11px',
                                              height: '24px',
                                              padding: '0 8px',
                                            }}
                                          />
                                        )}
                                        {contractTitle && (
                                          <Chip
                                            label={contractTitle}
                                            size="small"
                                            sx={{ 
                                              fontSize: '11px',
                                              height: '24px',
                                              padding: '0 8px',
                                            }}
                                          />
                                        )}
                                      </>
                                    );
                                  })()}
                                </Box>
                              </Box>
                            </Box>
                          </Paper>
                        </SwipeableListItem>
                      ))}
                    </Box>
                  );
                })}
              </Box>
            );
            })()}
          </Box>
        )}

        {/* 빠른 추가 버튼 (FAB) - 터치 최적화 */}
        <Fab
          color="primary"
          aria-label="add"
          onClick={() => {
            // 햅틱 피드백
            if (navigator.vibrate) {
              navigator.vibrate(20);
            }
            handleAddRowMobile();
          }}
          sx={{
            position: 'fixed',
            bottom: '104px', // 하단 네비게이션 위
            right: '20px',
            backgroundColor: colors.accent,
            '&:hover': {
              backgroundColor: colors.accent,
              opacity: 0.9,
            },
            '&:active': {
              transform: 'scale(0.95)',
            },
            minWidth: '64px',
            minHeight: '64px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.25)',
            zIndex: 1100,
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
        >
          <Plus size={28} />
        </Fab>

        {/* 투입시간 반영 다이얼로그 - 모바일 최적화 */}
        <Dialog
          open={logDialogOpen}
          onClose={() => setLogDialogOpen(false)}
          fullWidth
          maxWidth="sm"
          fullScreen={false}
          PaperProps={{
            sx: {
              borderRadius: 3,
              backgroundColor: isDark ? '#1C1D22' : '#FFFFFF',
              margin: '16px',
              maxHeight: 'calc(100% - 32px)',
            },
          }}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleCreateLog();
            }}
          >
            <DialogTitle 
              sx={{ 
                color: colors.primaryText,
                fontSize: '14px',
                lineHeight: 1.5,
                fontWeight: 600,
                pb: 1,
              }}
            >
              투입시간 반영
            </DialogTitle>
            <DialogContent>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
                <TextField
                  type="date"
                  label="업무일자"
                  value={logFormData.log_date}
                  onChange={(e) => setLogFormData({ ...logFormData, log_date: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end" sx={{ mr: -0.5 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleLogDateStep(1)}
                            sx={{ p: 0.25, color: colors.secondaryText }}
                            aria-label="업무일자 하루 증가"
                          >
                            <ChevronUp size={14} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleLogDateStep(-1)}
                            sx={{ p: 0.25, color: colors.secondaryText }}
                            aria-label="업무일자 하루 감소"
                          >
                            <ChevronDown size={14} />
                          </IconButton>
                        </Box>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiInputBase-root': {
                      fontSize: '12px',
                      minHeight: '48px',
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '12px',
                    },
                  }}
                />
                <TextField
                  label="투입시간(분)"
                  type="number"
                  value={logFormData.input_minute}
                  onChange={(e) => setLogFormData({ ...logFormData, input_minute: parseInt(e.target.value) || 0 })}
                  fullWidth
                  required
                  sx={{
                    '& .MuiInputBase-root': {
                      fontSize: '12px',
                      minHeight: '48px',
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '12px',
                    },
                  }}
                />
                <TextField
                  label="상세내용"
                  value={logFormData.input_description}
                  onChange={(e) => setLogFormData({ ...logFormData, input_description: e.target.value })}
                  multiline
                  rows={4}
                  fullWidth
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                      e.preventDefault();
                      handleCreateLog();
                    }
                  }}
                  sx={{
                    '& .MuiInputBase-root': {
                      fontSize: '12px',
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: '12px',
                    },
                  }}
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2.5, gap: 1.5 }}>
              <Button 
                type="button"
                onClick={() => setLogDialogOpen(false)}
                sx={{
                  minHeight: '48px',
                  fontSize: '12px',
                  padding: '12px 24px',
                }}
              >
                취소
              </Button>
              <Button
                type="submit"
                variant="contained"
                sx={{ 
                  backgroundColor: colors.accent,
                  minHeight: '48px',
                  fontSize: '12px',
                  padding: '12px 24px',
                  fontWeight: 600,
                }}
              >
                반영
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Bottom Sheet (추가/수정) */}
        <TodoBottomSheet
          open={bottomSheetOpen}
          onClose={() => {
            setBottomSheetOpen(false);
            setEditingTodo(null);
          }}
          onSubmit={handleSubmitTodo}
          initialData={editingTodo}
          companies={companies}
          contracts={contracts}
        />

        {/* 삭제 확인 다이얼로그 */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={() => {
            setDeleteConfirmOpen(false);
            setTodoToDelete(null);
          }}
          PaperProps={{
            sx: {
              borderRadius: 3,
              p: 2,
            },
          }}
        >
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (todoToDelete) {
                confirmDelete();
              }
            }}
          >
            <DialogTitle sx={{ fontSize: '14px', fontWeight: 600 }}>
              정말 삭제하시겠습니까?
            </DialogTitle>
            <DialogContent>
              <Typography sx={{ fontSize: '12px', color: colors.secondaryText }}>
                이 To-Do 항목을 삭제합니다.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ gap: 1, px: 2, pb: 2 }}>
              <Button
                type="button"
                onClick={() => {
                  setDeleteConfirmOpen(false);
                  setTodoToDelete(null);
                }}
                variant="outlined"
                sx={{ minHeight: '44px', fontSize: '12px' }}
              >
                취소
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="error"
                sx={{ minHeight: '44px', fontSize: '12px' }}
              >
                삭제
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Snackbar (Undo 기능) */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={5000}
          onClose={() => {
            setSnackbarOpen(false);
            setDeletedTodo(null);
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => {
              setSnackbarOpen(false);
              setDeletedTodo(null);
            }}
            severity="success"
            sx={{ width: '100%', fontSize: '12px' }}
            action={
              deletedTodo ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={handleUndoDelete}
                  sx={{ fontSize: '12px', fontWeight: 600 }}
                >
                  되돌리기
                </Button>
              ) : null
            }
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>

        {/* 삭제 확인 모달 */}
        <ConfirmModal
          isOpen={deleteConfirmOpen}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          title="삭제 확인"
          message={deleteConfirmMessage}
          confirmText="삭제"
          cancelText="취소"
          confirmColor={colors.error || '#f44336'}
          theme={isDark ? 'dark' : 'light'}
        />
      </Box>
    );
  }

  // PC 뷰 렌더링 (기존 코드)
  return (
    <Box sx={{ p: 1.5, pt: 4, minHeight: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
      {/* 다중 행 입력 섹션 (PC 버전) */}
      {!isMobileView && (
        <Card sx={{ mb: 2, backgroundColor: isDark ? '#1C1D22' : '#F8F9FA' }}>
          <CardContent>
            <Box>
              {/* 상단 액션 바 */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckSquare size={18} />
                  <Typography variant="h6" sx={{ color: colors.primaryText, fontWeight: 600 }}>
                    To-Do 입력
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="body2" sx={{ color: colors.secondaryText, minWidth: 100 }}>
                    {selectedRowIds.length}개 행 선택됨
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<Plus size={16} />}
                    onClick={handleAddRow}
                    size="small"
                    sx={{ 
                      borderColor: colors.accent,
                      color: colors.accent,
                      height: '32px',
                    }}
                  >
                    추가
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<Trash2 size={16} />}
                    onClick={handleDeleteSelectedRows}
                    disabled={selectedRowIds.length === 0}
                    size="small"
                    sx={{
                      borderColor: selectedRowIds.length === 0 ? colors.secondaryText : (colors.error || '#f44336'),
                      color: selectedRowIds.length === 0 ? colors.secondaryText : (colors.error || '#f44336'),
                      height: '32px',
                      opacity: selectedRowIds.length === 0 ? 0.5 : 1,
                    }}
                  >
                    삭제
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      const allSelected = inputRows.length > 0 && selectedRowIds.length === inputRows.length;
                      handleSelectAll(!allSelected);
                    }}
                    size="small"
                    sx={{
                      borderColor: colors.secondaryText,
                      color: colors.secondaryText,
                      height: '32px',
                      fontSize: '12px',
                    }}
                  >
                    {inputRows.length > 0 && selectedRowIds.length === inputRows.length ? '전체 해제' : '전체 선택'}
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Save size={16} />}
                    onClick={handleBatchSubmit}
                    sx={{
                      backgroundColor: colors.accent,
                      minWidth: 100,
                      height: '32px',
                      fontSize: '14px',
                      fontWeight: 600,
                    }}
                  >
                    저장
                  </Button>
                </Box>
              </Box>

              {/* 다중 행 입력 테이블 */}
              <Box 
                sx={{ 
                  mb: 2,
                  p: 2,
                  backgroundColor: isDark ? 'transparent' : '#F8F9FA',
                  borderRadius: 2,
                  border: `1px solid ${colors.primaryBorder}`,
                }}
              >
                {inputRows.map((row, index) => {
                  const rowContractOptions = [
                    ...(recentContracts.map(c => ({ ...c, isRecent: true, display_label: `${c.company_name_kr || ''} - ${c.contract_title || c.contract_id || ''}` }))),
                    ...(recentContracts.length > 0 && contractOptions.length > 0 ? [{ divider: true }] : []),
                    ...(contractOptions.map(c => ({ ...c, display_label: `${c.company_name_kr || ''} - ${c.contract_title || c.contract_id || ''}` })))
                  ];
                  const isSelected = selectedRowIds.includes(row.id);

                  return (
                    <Box
                      key={row.id}
                      sx={{
                        display: 'flex',
                        gap: 1.5,
                        alignItems: 'flex-start',
                        p: 1.5,
                        backgroundColor: isSelected 
                          ? (isDark ? 'transparent' : 'rgba(0, 0, 0, 0.05)') 
                          : 'transparent',
                        borderRadius: 1,
                        borderBottom: index < inputRows.length - 1 ? `1px solid ${colors.primaryBorder}` : 'none',
                      }}
                    >
                      {/* 체크박스 */}
                      <Box sx={{ pt: 0.5 }}>
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handleRowSelect(row.id)}
                          size="small"
                          sx={{
                            color: colors.accent,
                            '&.Mui-checked': {
                              color: colors.accent,
                            },
                          }}
                        />
                      </Box>

                      {/* 입력 필드들 */}
                      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'nowrap', flex: 1, alignItems: 'flex-start' }}>
                        {/* 통합 계약 검색 */}
                        <Autocomplete
                          options={rowContractOptions}
                          getOptionLabel={(option) => {
                            if (option.divider) return '';
                            return option.display_label || `${option.company_name_kr || ''} - ${option.contract_title || option.contract_id || ''}`;
                          }}
                          value={row.contract || null}
                          onChange={(_, newValue) => {
                            if (newValue && !newValue.divider) {
                              handleRowContractSelect(row.id, newValue);
                            }
                          }}
                          onInputChange={(_, newInputValue) => {
                            if (newInputValue.length >= 2) {
                              searchContracts(newInputValue);
                            }
                          }}
                          loading={isSearchingContracts}
                          size="small"
                          sx={{ minWidth: 280, maxWidth: 350, flex: '1 1 280px' }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="업체/계약 검색"
                              placeholder="업체명 또는 계약명을 입력하세요"
                              inputProps={{
                                ...params.inputProps,
                                'data-row-id': row.id,
                                'data-field': 'contract',
                              }}
                              onKeyDown={(e) => {
                                // Tab: 다음 필드(업무 구분)로 이동
                                if (e.key === 'Tab' && !e.shiftKey) {
                                  e.preventDefault();
                                  setTimeout(() => {
                                    const nextField = document.querySelector(`input[data-row-id="${row.id}"][data-field="job_category"]`);
                                    if (nextField) {
                                      nextField.focus();
                                    }
                                  }, 10);
                                }
                                // Shift+Tab: 이전 행의 마지막 필드(비고)로 이동
                                else if (e.key === 'Tab' && e.shiftKey) {
                                  e.preventDefault();
                                  if (index > 0) {
                                    setTimeout(() => {
                                      const prevRowField = document.querySelector(`input[data-row-id="${inputRows[index - 1].id}"][data-field="note_1"]`);
                                      if (prevRowField) {
                                        prevRowField.focus();
                                      }
                                    }, 10);
                                  }
                                }
                              }}
                              InputProps={{
                                ...params.InputProps,
                                startAdornment: (
                                  <>
                                    <Search size={18} style={{ marginRight: 8, color: colors.secondaryText }} />
                                    {params.InputProps.startAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          renderOption={(props, option) => {
                            if (option.divider) {
                              return <Divider key="divider" />;
                            }
                            return (
                              <ListItem {...props} key={option.contract_id}>
                                {option.isRecent && (
                                  <Chip
                                    label="최근 사용"
                                    size="small"
                                    sx={{ mr: 1, height: 20, fontSize: '0.7rem' }}
                                    color="primary"
                                  />
                                )}
                                <ListItemText
                                  primary={option.display_label || `${option.company_name_kr || ''} - ${option.contract_title || option.contract_id || ''}`}
                                  secondary={option.contract_id}
                                />
                              </ListItem>
                            );
                          }}
                          noOptionsText="2자 이상 입력하세요"
                        />

                        {/* 업무 구분 */}
                        <TextField
                          label="업무 구분"
                          value={row.job_category || ''}
                          onChange={(e) => handleRowChange(row.id, 'job_category', e.target.value)}
                          size="small"
                          placeholder="예: 정산, 정정, 보고서 (자유 입력 가능)"
                          inputProps={{
                            'data-row-id': row.id,
                            'data-field': 'job_category',
                          }}
                          onKeyDown={(e) => {
                            // Ctrl+Enter 또는 Cmd+Enter로 일괄 저장
                            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                              e.preventDefault();
                              e.stopPropagation();
                              handleBatchSubmit();
                            }
                            // Tab: 다음 필드(업무 상세내용)로 이동
                            else if (e.key === 'Tab' && !e.shiftKey) {
                              e.preventDefault();
                              setTimeout(() => {
                                const nextField = document.querySelector(`input[data-row-id="${row.id}"][data-field="work_description"]`);
                                if (nextField) {
                                  nextField.focus();
                                }
                              }, 10);
                            }
                            // Shift+Tab: 이전 필드(업체/계약 검색)로 이동
                            else if (e.key === 'Tab' && e.shiftKey) {
                              e.preventDefault();
                              setTimeout(() => {
                                const prevField = document.querySelector(`input[data-row-id="${row.id}"][data-field="contract"]`);
                                if (prevField) {
                                  prevField.focus();
                                }
                              }, 10);
                            }
                          }}
                          sx={{ minWidth: 150, flex: '1 1 150px' }}
                        />

                        {/* 업무 상세내용 */}
                        <TextField
                          label="업무 상세내용"
                          value={row.work_description}
                          onChange={(e) => handleRowWorkDescriptionChange(row.id, e.target.value)}
                          size="small"
                          placeholder="업무 내용을 상세히 입력하세요 (Ctrl+Enter로 저장)"
                          inputProps={{
                            'data-row-id': row.id,
                            'data-field': 'work_description',
                          }}
                          onKeyDown={(e) => {
                            // Ctrl+Enter 또는 Cmd+Enter로 일괄 저장
                            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                              e.preventDefault();
                              e.stopPropagation();
                              handleBatchSubmit();
                            }
                            // Tab: 다음 필드(비고)로 이동
                            else if (e.key === 'Tab' && !e.shiftKey) {
                              e.preventDefault();
                              setTimeout(() => {
                                const nextField = document.querySelector(`input[data-row-id="${row.id}"][data-field="note_1"]`);
                                if (nextField) {
                                  nextField.focus();
                                }
                              }, 10);
                            }
                            // Shift+Tab: 이전 필드(업무 구분)로 이동
                            else if (e.key === 'Tab' && e.shiftKey) {
                              e.preventDefault();
                              setTimeout(() => {
                                const prevField = document.querySelector(`input[data-row-id="${row.id}"][data-field="job_category"]`);
                                if (prevField) {
                                  prevField.focus();
                                }
                              }, 10);
                            }
                          }}
                          sx={{ minWidth: 200, flex: '2 1 200px' }}
                          required
                        />

                        {/* 비고 */}
                        <TextField
                          label="비고"
                          value={row.note_1}
                          onChange={(e) => handleRowChange(row.id, 'note_1', e.target.value)}
                          size="small"
                          placeholder="업무 기한 등 참고사항을 입력하세요 (Ctrl+Enter로 저장)"
                          inputProps={{
                            'data-row-id': row.id,
                            'data-field': 'note_1',
                          }}
                          onKeyDown={(e) => {
                            // Ctrl+Enter 또는 Cmd+Enter로 일괄 저장
                            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                              e.preventDefault();
                              e.stopPropagation();
                              handleBatchSubmit();
                            }
                            // Shift+Tab: 이전 필드(업무 상세내용)로 이동
                            else if (e.key === 'Tab' && e.shiftKey) {
                              e.preventDefault();
                              setTimeout(() => {
                                const prevField = document.querySelector(`input[data-row-id="${row.id}"][data-field="work_description"]`);
                                if (prevField) {
                                  prevField.focus();
                                }
                              }, 10);
                            }
                            // Tab: 마지막 행이면 행 추가, 아니면 다음 행의 첫 번째 필드로 이동
                            else if (e.key === 'Tab' && !e.shiftKey) {
                              e.preventDefault();
                              if (index === inputRows.length - 1) {
                                // 마지막 행에서 Tab 키를 누르면 행 추가
                                const newRowId = handleAddRow();
                                // 새로 추가된 행의 업체/계약 검색으로 포커스 이동
                                setTimeout(() => {
                                  const inputElement = document.querySelector(`input[data-row-id="${newRowId}"][data-field="contract"]`);
                                  if (inputElement) {
                                    inputElement.focus();
                                  }
                                }, 100);
                              } else {
                                // 다음 행의 첫 번째 필드(업체/계약 검색)로 이동
                                setTimeout(() => {
                                  const nextRowField = document.querySelector(`input[data-row-id="${inputRows[index + 1].id}"][data-field="contract"]`);
                                  if (nextRowField) {
                                    nextRowField.focus();
                                  }
                                }, 10);
                              }
                            }
                          }}
                          sx={{ minWidth: 200, flex: '1 1 200px' }}
                        />
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* To-Do 리스트 테이블 - 투입시간 목록과 동일한 포맷 */}
      <Card sx={{ display: 'flex', flexDirection: 'column', borderRadius: 0 }}>
        <CardContent sx={{ display: 'flex', flexDirection: 'column', p: 2, '&:last-child': { pb: 2 } }}>
          {/* 헤더 - 투입시간 목록과 동일한 포맷 */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            {/* 좌측: 타이틀 및 필터 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Grid size={18} />
                <Typography variant="h6" sx={{ color: colors.primaryText, fontWeight: 600 }}>
                  To-Do 리스트
                </Typography>
                <Tooltip title="목록 새로고침">
                  <IconButton
                    size="small"
                    onClick={async () => {
                      await fetchTodos();
                    }}
                    sx={{
                      color: colors.accent,
                      '&:hover': {
                        backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                      },
                    }}
                  >
                    <RefreshCw size={18} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            {/* 우측: 완료 포함 체크박스, 통계 정보 및 엑셀 다운로드 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* 완료 포함 체크박스 */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeCompleted}
                    onChange={(e) => setIncludeCompleted(e.target.checked)}
                    size="small"
                  />
                }
                label="완료 포함"
              />
              {/* 통계 정보 */}
              <Typography variant="body2" sx={{ color: colors.secondaryText }}>
                총 <strong style={{ color: colors.primaryText }}>{activeTodos.length}</strong>건
              </Typography>
              {/* 엑셀 다운로드 버튼 */}
              <Tooltip title="엑셀 다운로드">
                <span>
                  <IconButton
                    size="small"
                    onClick={handleDownloadExcel}
                    disabled={sortedTodos.length === 0}
                    sx={{
                      color: sortedTodos.length === 0 ? colors.secondaryText : colors.accent,
                      '&:hover': {
                        backgroundColor: sortedTodos.length === 0 ? 'transparent' : (isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'),
                      },
                      '&.Mui-disabled': {
                        color: colors.secondaryText,
                        opacity: 0.5,
                      },
                    }}
                  >
                    <Download size={18} />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
          {/* 조회 기간 선택 (PC 버전) */}
          <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* 빠른 선택 배지 */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <Typography variant="body2" sx={{ color: colors.secondaryText, fontSize: '13px', mr: 0.5 }}>
                조회 기간:
              </Typography>
              <Chip
                label="일주일"
                onClick={() => {
                  const today = new Date();
                  const weekStart = startOfWeek(today, { weekStartsOn: 1 });
                  const weekEnd = endOfWeek(today, { weekStartsOn: 1 });
                  setDateRange({
                    startDate: format(weekStart, 'yyyy-MM-dd'),
                    endDate: format(weekEnd, 'yyyy-MM-dd'),
                  });
                }}
                size="small"
                sx={{
                  fontSize: '12px',
                  height: '28px',
                  cursor: 'pointer',
                  backgroundColor: dateRange.startDate && dateRange.endDate && 
                    format(new Date(dateRange.startDate), 'yyyy-MM-dd') === format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd') &&
                    format(new Date(dateRange.endDate), 'yyyy-MM-dd') === format(endOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd')
                    ? colors.accent
                    : isDark ? '#2A2B30' : '#F0F0F0',
                  color: dateRange.startDate && dateRange.endDate && 
                    format(new Date(dateRange.startDate), 'yyyy-MM-dd') === format(startOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd') &&
                    format(new Date(dateRange.endDate), 'yyyy-MM-dd') === format(endOfWeek(new Date(), { weekStartsOn: 1 }), 'yyyy-MM-dd')
                    ? '#FFFFFF'
                    : colors.primaryText,
                  '&:hover': {
                    backgroundColor: colors.accent,
                    color: '#FFFFFF',
                  },
                }}
              />
              <Chip
                label="한달"
                onClick={() => {
                  const today = new Date();
                  const monthStart = startOfMonth(today);
                  const monthEnd = endOfMonth(today);
                  setDateRange({
                    startDate: format(monthStart, 'yyyy-MM-dd'),
                    endDate: format(monthEnd, 'yyyy-MM-dd'),
                  });
                }}
                size="small"
                sx={{
                  fontSize: '12px',
                  height: '28px',
                  cursor: 'pointer',
                  backgroundColor: dateRange.startDate && dateRange.endDate && 
                    format(new Date(dateRange.startDate), 'yyyy-MM-dd') === format(startOfMonth(new Date()), 'yyyy-MM-dd') &&
                    format(new Date(dateRange.endDate), 'yyyy-MM-dd') === format(endOfMonth(new Date()), 'yyyy-MM-dd')
                    ? colors.accent
                    : isDark ? '#2A2B30' : '#F0F0F0',
                  color: dateRange.startDate && dateRange.endDate && 
                    format(new Date(dateRange.startDate), 'yyyy-MM-dd') === format(startOfMonth(new Date()), 'yyyy-MM-dd') &&
                    format(new Date(dateRange.endDate), 'yyyy-MM-dd') === format(endOfMonth(new Date()), 'yyyy-MM-dd')
                    ? '#FFFFFF'
                    : colors.primaryText,
                  '&:hover': {
                    backgroundColor: colors.accent,
                    color: '#FFFFFF',
                  },
                }}
              />
              <Chip
                label="전체"
                onClick={() => {
                  setDateRange({ startDate: null, endDate: null });
                }}
                size="small"
                sx={{
                  fontSize: '12px',
                  height: '28px',
                  cursor: 'pointer',
                  backgroundColor: !dateRange.startDate && !dateRange.endDate
                    ? colors.accent
                    : isDark ? '#2A2B30' : '#F0F0F0',
                  color: !dateRange.startDate && !dateRange.endDate
                    ? '#FFFFFF'
                    : colors.primaryText,
                  '&:hover': {
                    backgroundColor: colors.accent,
                    color: '#FFFFFF',
                  },
                }}
              />
            </Box>
            {/* 시작일~종료일 선택 */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                type="date"
                label="시작일"
                value={dateRange.startDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment 
                      position="end" 
                      sx={{ 
                        mr: -0.5,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRangeDateStep('start', 1);
                          }}
                          sx={{ 
                            p: 0.25, 
                            color: colors.secondaryText,
                            minWidth: '20px',
                            width: '20px',
                            height: '16px',
                          }}
                          aria-label="시작일 하루 증가"
                        >
                          <ChevronUp size={14} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRangeDateStep('start', -1);
                          }}
                          sx={{ 
                            p: 0.25, 
                            color: colors.secondaryText,
                            minWidth: '20px',
                            width: '20px',
                            height: '16px',
                          }}
                          aria-label="시작일 하루 감소"
                        >
                          <ChevronDown size={14} />
                        </IconButton>
                      </Box>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: '180px',
                  '& .MuiInputBase-root': {
                    fontSize: '14px',
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '13px',
                  },
                  '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                    filter: isDark ? 'invert(1)' : 'none',
                    opacity: 1,
                    cursor: 'pointer',
                  },
                  '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator:hover': {
                    opacity: 0.7,
                  },
                }}
                inputProps={{
                  sx: {
                    color: colors.primaryText,
                    '&::-webkit-calendar-picker-indicator': {
                      filter: isDark ? 'invert(1)' : 'none',
                    },
                  }
                }}
              />
              <Typography variant="body2" sx={{ color: colors.secondaryText, fontSize: '14px' }}>
                ~
              </Typography>
              <TextField
                type="date"
                label="종료일"
                value={dateRange.endDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment 
                      position="end" 
                      sx={{ 
                        mr: -0.5,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRangeDateStep('end', 1);
                          }}
                          sx={{ 
                            p: 0.25, 
                            color: colors.secondaryText,
                            minWidth: '20px',
                            width: '20px',
                            height: '16px',
                          }}
                          aria-label="종료일 하루 증가"
                        >
                          <ChevronUp size={14} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRangeDateStep('end', -1);
                          }}
                          sx={{ 
                            p: 0.25, 
                            color: colors.secondaryText,
                            minWidth: '20px',
                            width: '20px',
                            height: '16px',
                          }}
                          aria-label="종료일 하루 감소"
                        >
                          <ChevronDown size={14} />
                        </IconButton>
                      </Box>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  width: '180px',
                  '& .MuiInputBase-root': {
                    fontSize: '14px',
                  },
                  '& .MuiInputLabel-root': {
                    fontSize: '13px',
                  },
                  '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator': {
                    filter: isDark ? 'invert(1)' : 'none',
                    opacity: 1,
                    cursor: 'pointer',
                  },
                  '& .MuiInputBase-input[type="date"]::-webkit-calendar-picker-indicator:hover': {
                    opacity: 0.7,
                  },
                }}
                inputProps={{
                  sx: {
                    color: colors.primaryText,
                    '&::-webkit-calendar-picker-indicator': {
                      filter: isDark ? 'invert(1)' : 'none',
                    },
                  }
                }}
              />
            </Box>
          </Box>
          <TableContainer 
            data-table-container="todo"
            sx={{ 
              overflow: 'auto',
              maxHeight: 'calc(100vh - 480px)',  // 상단 요소들을 제외한 최대 높이
              position: 'relative',
              width: '100%',
            }}
          >
            <Table 
              stickyHeader 
              size="small" 
              sx={{ 
                width: '100%', 
                borderCollapse: 'separate', 
                borderSpacing: 0, 
                fontSize: '14px', 
                '& .MuiTableCell-root': { fontSize: '14px' }, 
                tableLayout: 'fixed'
              }}
            >
              <TableHead sx={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: isDark ? '#1C1D22' : '#E8EAED' }}>
                <TableRow>
                  <TableCell
                    onClick={() => handleSort('todo_id')}
                    sx={{
                      width: getColumnWidth('todo_id'),
                      minWidth: '80px',
                      backgroundColor: isDark ? '#1C1D22' : '#E8EAED',
                      fontWeight: 600,
                      border: `1px dashed ${isDark ? '#555' : '#ccc'}`,
                      fontSize: '14px',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': {
                        backgroundColor: isDark ? '#2A2B30' : '#D0D3D6',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      ID
                      {sortConfig.column === 'todo_id' && (
                        sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      )}
                    </Box>
                  </TableCell>
                  {/* <TableCell
                    onClick={() => handleSort('company_code')}
                    sx={{
                      width: getColumnWidth('company_code'),
                      minWidth: '100px',
                      backgroundColor: isDark ? '#1C1D22' : '#E8EAED',
                      fontWeight: 600,
                      border: `1px dashed ${isDark ? '#555' : '#ccc'}`,
                      fontSize: '14px',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': {
                        backgroundColor: isDark ? '#2A2B30' : '#D0D3D6',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      업체코드
                      {sortConfig.column === 'company_code' && (
                        sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      )}
                    </Box>
                  </TableCell> */}
                  <TableCell
                    onClick={() => handleSort('company_code')}
                    sx={{
                      width: getColumnWidth('company_name'),
                      minWidth: '120px',
                      backgroundColor: isDark ? '#1C1D22' : '#E8EAED',
                      fontWeight: 600,
                      border: `1px dashed ${isDark ? '#555' : '#ccc'}`,
                      fontSize: '14px',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': {
                        backgroundColor: isDark ? '#2A2B30' : '#D0D3D6',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      업체명
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort('contract_id')}
                    sx={{
                      width: getColumnWidth('contract_id'),
                      minWidth: '110px',
                      backgroundColor: isDark ? '#1C1D22' : '#E8EAED',
                      fontWeight: 600,
                      border: `1px dashed ${isDark ? '#555' : '#ccc'}`,
                      fontSize: '14px',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': {
                        backgroundColor: isDark ? '#2A2B30' : '#D0D3D6',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      계약번호
                      {sortConfig.column === 'contract_id' && (
                        sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort('contract_id')}
                    sx={{
                      width: getColumnWidth('contract_title'),
                      minWidth: '120px',
                      backgroundColor: isDark ? '#1C1D22' : '#E8EAED',
                      fontWeight: 600,
                      border: `1px dashed ${isDark ? '#555' : '#ccc'}`,
                      fontSize: '14px',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': {
                        backgroundColor: isDark ? '#2A2B30' : '#D0D3D6',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      계약 명칭
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort('job_category')}
                    sx={{
                      width: getColumnWidth('job_category'),
                      minWidth: '100px',
                      backgroundColor: isDark ? '#1C1D22' : '#E8EAED',
                      fontWeight: 600,
                      border: `1px dashed ${isDark ? '#555' : '#ccc'}`,
                      fontSize: '14px',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': {
                        backgroundColor: isDark ? '#2A2B30' : '#D0D3D6',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      업무 구분
                      {sortConfig.column === 'job_category' && (
                        sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort('note_1')}
                    sx={{
                      width: getColumnWidth('note_1'),
                      minWidth: '160px',
                      backgroundColor: isDark ? '#1C1D22' : '#E8EAED',
                      fontWeight: 600,
                      border: `1px dashed ${isDark ? '#555' : '#ccc'}`,
                      fontSize: '14px',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': {
                        backgroundColor: isDark ? '#2A2B30' : '#D0D3D6',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      업무내용 상세
                      {sortConfig.column === 'note_1' && (
                        sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      width: getColumnWidth('note_2'),
                      minWidth: '120px',
                      backgroundColor: isDark ? '#1C1D22' : '#E8EAED',
                      fontWeight: 600,
                      border: `1px dashed ${isDark ? '#555' : '#ccc'}`,
                      fontSize: '14px',
                    }}
                  >
                    비고
                  </TableCell>
                  <TableCell
                    onClick={() => handleSort('created_at')}
                    sx={{
                      width: getColumnWidth('created_at'),
                      minWidth: '120px',
                      backgroundColor: isDark ? '#1C1D22' : '#E8EAED',
                      fontWeight: 600,
                      border: `1px dashed ${isDark ? '#555' : '#ccc'}`,
                      fontSize: '14px',
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': {
                        backgroundColor: isDark ? '#2A2B30' : '#D0D3D6',
                      },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      생성일시
                      {sortConfig.column === 'created_at' && (
                        sortConfig.direction === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      width: getColumnWidth('status'),
                      minWidth: '120px',
                      backgroundColor: isDark ? '#1C1D22' : '#E8EAED',
                      fontWeight: 600,
                      border: `1px dashed ${isDark ? '#555' : '#ccc'}`,
                      fontSize: '14px',
                    }}
                  >
                    진행경과
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      width: getColumnWidth('actions'),
                      minWidth: '120px',
                      backgroundColor: isDark ? '#1C1D22' : '#E8EAED',
                      fontWeight: 600,
                      border: `1px dashed ${isDark ? '#555' : '#ccc'}`,
                      fontSize: '14px',
                    }}
                  >
                    비고
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {activeTodos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4, color: colors.secondaryText, fontSize: '14px' }}>
                      등록된 To-Do가 없습니다.
                    </TableCell>
                  </TableRow>
                ) : (
                  activeTodos.map((row) => {
                    const company = companies.find(c => c.company_code === row.company_code);
                    const contract = contracts.find(c => c.contract_id === row.contract_id);
                    const isEditing = editingRowId === row.todo_id;
                    return (
                      <ResizableTableRow
                        key={row.todo_id}
                        rowId={row.todo_id}
                        height={rowHeights[row.todo_id] || 48}
                        onHeightChange={handleRowHeightChange}
                        onDoubleClick={() => handleRowDoubleClick(row.todo_id)}
                        tabIndex={0}
                        data-row-id={row.todo_id}
                        onKeyDown={(e) => {
                          if (e.key === 'F2') {
                            e.preventDefault();
                            if (isEditing) {
                              // 이미 편집 모드인 경우: 첫 번째 셀에 커서 활성화
                              handleCellClick(row.todo_id, 'note_1', row.note_1);
                            } else {
                              // 편집 모드가 아닌 경우: 편집 모드로 전환
                              setEditingRowId(row.todo_id);
                            }
                          }
                        }}
                        sx={{
                          '&:hover': {
                            backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
                          },
                          '&:focus': {
                            outline: `2px solid ${colors.accent}`,
                            outlineOffset: '-2px',
                            backgroundColor: isDark ? 'rgba(138,124,255,0.05)' : 'rgba(138,124,255,0.05)',
                          },
                          opacity: row.status === 'completed' ? 0.6 : 1,
                          textDecoration: row.status === 'completed' ? 'line-through' : 'none',
                          cursor: isEditing ? 'default' : 'pointer',
                        }}
                      >
                        <TableCell sx={{ 
                          border: `1px dashed ${isDark ? '#555' : '#ccc'}`, 
                          fontSize: '14px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: 'default',
                        }}>
                          {row.todo_id}
                        </TableCell>
                        {/* <TableCell 
                          sx={{ 
                            border: `1px dashed ${isDark ? '#555' : '#ccc'}`, 
                            fontSize: '14px',
                            cursor: isEditing ? 'pointer' : 'default',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          onClick={() => isEditing && handleCellClick(row.todo_id, 'company_code', row.company_code)}
                        >
                          {isEditing ? renderEditableCell(row, 'company_code', row.company_code) : (company?.company_name_kr || company?.company_name || row.company_code || '-')}
                        </TableCell> */}
                        <TableCell sx={{ 
                          border: `1px dashed ${isDark ? '#555' : '#ccc'}`, 
                          fontSize: '14px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: 'default',
                        }}>
                          {company?.company_name_kr || company?.company_name || '-'}
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            border: `1px dashed ${isDark ? '#555' : '#ccc'}`, 
                            fontSize: '14px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            cursor: 'default',
                          }}
                        >
                          {row.contract_id || '-'}
                        </TableCell>
                        <TableCell sx={{ 
                          border: `1px dashed ${isDark ? '#555' : '#ccc'}`, 
                          fontSize: '14px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: 'default',
                        }}>
                          {contract?.contract_title || '-'}
                        </TableCell>
                        <TableCell sx={{ 
                          border: `1px dashed ${isDark ? '#555' : '#ccc'}`, 
                          fontSize: '14px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: 'default',
                        }}>
                          {row.job_category || '-'}
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            border: `1px dashed ${isDark ? '#555' : '#ccc'}`,
                            fontSize: '14px',
                            cursor: isEditing ? 'pointer' : 'default',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            // 편집 가능한 필드에 음영 효과 추가
                            backgroundColor: isEditing 
                              ? (isDark ? 'rgba(138,124,255,0.1)' : 'rgba(138,124,255,0.05)')
                              : 'transparent',
                            transition: 'all 0.2s ease',
                            '&:hover': isEditing ? {
                              backgroundColor: isDark ? 'rgba(138,124,255,0.15)' : 'rgba(138,124,255,0.1)',
                            } : {},
                          }}
                          onClick={() => isEditing && handleCellClick(row.todo_id, 'note_1', row.note_1)}
                        >
                          {isEditing ? renderEditableCell(row, 'note_1', row.note_1) : (row.note_1 || '-')}
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            border: `1px dashed ${isDark ? '#555' : '#ccc'}`,
                            fontSize: '14px',
                            cursor: isEditing ? 'pointer' : 'default',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            // 편집 가능한 필드에 음영 효과 추가
                            backgroundColor: isEditing 
                              ? (isDark ? 'rgba(138,124,255,0.1)' : 'rgba(138,124,255,0.05)')
                              : 'transparent',
                            transition: 'all 0.2s ease',
                            '&:hover': isEditing ? {
                              backgroundColor: isDark ? 'rgba(138,124,255,0.15)' : 'rgba(138,124,255,0.1)',
                            } : {},
                          }}
                          onClick={() => isEditing && handleCellClick(row.todo_id, 'note_2', row.note_2)}
                        >
                          {isEditing ? renderEditableCell(row, 'note_2', row.note_2) : (row.note_2 || '-')}
                        </TableCell>
                        <TableCell sx={{ 
                          border: `1px dashed ${isDark ? '#555' : '#ccc'}`, 
                          fontSize: '14px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          cursor: 'default',
                        }}>
                          {row.created_at ? format(new Date(row.created_at), 'yyyy-MM-dd HH:mm') : '-'}
                        </TableCell>
                        <TableCell sx={{ 
                          border: `1px dashed ${isDark ? '#555' : '#ccc'}`,
                          cursor: 'default',
                        }}>
                          <FormControl size="small" variant="standard" sx={{ minWidth: 90, maxWidth: 110 }}>
                            <Select
                              value={row.status || 'active'}
                              onChange={(e) => handleToggleStatus(row.todo_id, e.target.value)}
                              disableUnderline
                              sx={{
                                fontSize: '14px',
                                height: '32px',
                                width: '100%',
                                border: 'none',
                                '& .MuiSelect-select': {
                                  py: 0.5,
                                  paddingRight: '32px !important',
                                },
                              }}
                            >
                              <MenuItem value="active">진행 중</MenuItem>
                              <MenuItem value="completed">완료</MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell align="center" sx={{ 
                          border: `1px dashed ${isDark ? '#555' : '#ccc'}`,
                          cursor: 'default',
                        }}>
                          {isEditing ? (
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                              <Tooltip title="저장">
                                <IconButton
                                  size="small"
                                  onMouseDown={(e) => {
                                    e.preventDefault(); // blur 이벤트 방지
                                  }}
                                  onClick={() => handleSaveClick(row.todo_id)}
                                  sx={{ color: colors.accent }}
                                >
                                  <Save size={16} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="취소">
                                <IconButton
                                  size="small"
                                  onClick={handleCancelEdit}
                                  sx={{ color: colors.secondaryText }}
                                >
                                  <X size={16} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                              <Tooltip title="수정">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditClick(row.todo_id)}
                                  sx={{ color: colors.accent }}
                                >
                                  <Edit2 size={16} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="삭제">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(row.todo_id)}
                                  sx={{ color: colors.error }}
                                >
                                  <Trash2 size={16} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          )}
                        </TableCell>
                      </ResizableTableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>


      {/* 투입시간 반영 다이얼로그 */}
      {logDialogOpen && selectedTodoForLog && (
        <Card
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 1300,
            minWidth: 400,
            p: 3,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            투입시간 반영
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              type="date"
              label="업무일자"
              value={logFormData.log_date}
              onChange={(e) => setLogFormData({ ...logFormData, log_date: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ mr: -0.5 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                      <IconButton
                        size="small"
                        onClick={() => handleLogDateStep(1)}
                        sx={{ p: 0.25, color: colors.secondaryText }}
                        aria-label="업무일자 하루 증가"
                      >
                        <ChevronUp size={14} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleLogDateStep(-1)}
                        sx={{ p: 0.25, color: colors.secondaryText }}
                        aria-label="업무일자 하루 감소"
                      >
                        <ChevronDown size={14} />
                      </IconButton>
                    </Box>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="투입시간(분)"
              type="number"
              value={logFormData.input_minute}
              onChange={(e) => setLogFormData({ ...logFormData, input_minute: parseInt(e.target.value) || 0 })}
              fullWidth
              required
            />
            <TextField
              label="상세내용"
              value={logFormData.input_description}
              onChange={(e) => setLogFormData({ ...logFormData, input_description: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
              <Button onClick={() => setLogDialogOpen(false)}>취소</Button>
              <Button onClick={handleCreateLog} variant="contained" sx={{ backgroundColor: colors.accent }}>
                반영
              </Button>
            </Box>
          </Box>
        </Card>
      )}


      {/* 삭제 확인 모달 */}
      <ConfirmModal
        isOpen={deleteConfirmOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        title="삭제 확인"
        message={deleteConfirmMessage}
        confirmText="삭제"
        cancelText="취소"
        confirmColor={colors.error || '#f44336'}
        theme={isDark ? 'dark' : 'light'}
      />

      {/* Bottom Sheet (추가/수정) - PC 버전 */}
      <TodoBottomSheet
        open={bottomSheetOpen}
        onClose={() => {
          setBottomSheetOpen(false);
          setEditingTodo(null);
        }}
        onSubmit={handleSubmitTodo}
        initialData={editingTodo}
        companies={companies}
        contracts={contracts}
      />

      {/* 정보 모달 (저장 성공/알림) */}
      <InfoModal
        isOpen={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
        title={infoModalTitle}
        message={infoModalMessage}
        type="success"
      />
    </Box>
  );
};

export default TodoList;
